/** Complete v2 organization route catalogue, roles and realistic frontend fixtures. */

import { LANGUAGE_OPTIONS } from "../../../shared/languages";

export type RouteDef = {
  id: string;
  title: string;
  description: string;
  group: string;
  icon: string;
  badge?: string;
};

export type AreaDef = {
  id: string;
  label: string;
  shortLabel: string;
  icon: string;
  accent: string;
  routes: RouteDef[];
};

export type MetricSpec = { label: string; value: string; trend: string; icon: string };

export type ColumnSpec = { key: string; label: string };

export type WorkspaceRow = Record<string, string | number | boolean>;

export type WorkspaceSpec = {
  id: string;
  area: string;
  areaLabel: string;
  title: string;
  description: string;
  group: string;
  icon: string;
  layout: string;
  primaryAction: string;
  secondaryActions: string[];
  metrics: MetricSpec[];
  columns: ColumnSpec[];
  rows: WorkspaceRow[];
  tabs: string[];
  notes: Array<{ title: string; body: string }>;
  fields: string[];
  chart: number[];
  external?: { domain: string; mode: string } | null;
  signature: string;
  model: string;
  accessRoles?: string[];
  manageRoles?: string[];
  approveRoles?: string[];
};

export type RoleProfile = {
  id: string;
  name: string;
  scope: string;
  areas: string[];
  manage: string[];
  approve: string[];
};

export const AREAS: AreaDef[] = [
  {
    id: "command",
    label: "Organization Command",
    shortLabel: "Command",
    icon: "home",
    accent: "violet",
    routes: [
      {
        id: "command-dashboard",
        title: "Executive Capability Command",
        description:
          "A unified executive view of capability, learning delivery, adoption, governed AI use, commercial exposure and accountable next actions.",
        group: "Executive Command",
        icon: "home",
      },
      {
        id: "command-readiness",
        title: "AI Readiness Diagnostic",
        description:
          "Measure strategy, leadership, people, process, data, technology and governance readiness with evidence, owners and dated actions.",
        group: "Capability & Readiness",
        icon: "pulse",
      },
      {
        id: "command-adoption",
        title: "Adoption & Barrier Register",
        description:
          "Compare responsible AI adoption across teams and track barriers, interventions, owners, due dates and verified change.",
        group: "Adoption",
        icon: "chart",
      },
      {
        id: "command-outcomes",
        title: "Outcomes & Value Studio",
        description:
          "Separate expected, modeled and realized value while preserving assumptions, baselines, evidence sources and human approval.",
        group: "Outcomes & Value",
        icon: "target",
      },
      {
        id: "command-action-centre",
        title: "Cross-Organization Action Centre",
        description:
          "Route high-priority work from people, learning, Class, Labs, Build Rooms, governance, finance, integrations and support to named owners.",
        group: "Executive Command",
        icon: "bolt",
        badge: "12",
      },
    ],
  },
  {
    id: "people",
    label: "People & Access",
    shortLabel: "People",
    icon: "users",
    accent: "cyan",
    routes: [
      {
        id: "people-members",
        title: "Members, Seats & Invitations",
        description:
          "Manage organization members, Academy seats, invitation state, team placement, role scope and account lifecycle without exposing unrelated HR records.",
        group: "Membership",
        icon: "users",
        badge: "8",
      },
      {
        id: "people-teams",
        title: "Teams & Departments",
        description:
          "Model teams, departments, leaders, membership, learning sponsorship, adoption priorities and delegated reporting boundaries.",
        group: "Organization Structure",
        icon: "network",
      },
      {
        id: "people-learners",
        title: "Sponsored Learners",
        description:
          "Track sponsored learners from eligibility and invitation through cohort enrolment, participation, support signals, completion and offboarding.",
        group: "Learning Population",
        icon: "student",
        badge: "9",
      },
      {
        id: "people-instructors",
        title: "Instructors, Mentors & Assessors",
        description:
          "Coordinate assigned instructors, mentors and assessors, teaching workload, cohort coverage, Class access, Labs scope and review capacity.",
        group: "Teaching Capacity",
        icon: "teacher",
      },
      {
        id: "people-roles",
        title: "Roles, Permissions & Delegation",
        description:
          "Administer least-privilege roles, scoped authority, segregation of duties, expiry, MFA requirements and approval boundaries.",
        group: "Access Governance",
        icon: "lock",
      },
      {
        id: "people-access",
        title: "SSO, SCIM & Access Reviews",
        description:
          "Connect SSO and SCIM, map attributes, reconcile memberships, certify privileged access, revoke stale sessions and preserve access evidence.",
        group: "Identity Operations",
        icon: "key",
      },
    ],
  },
  {
    id: "learning",
    label: "Learning Operations",
    shortLabel: "Learning",
    icon: "book",
    accent: "green",
    routes: [
      {
        id: "learning-programs",
        title: "Program Portfolio",
        description:
          "Manage Academy programs selected for organizational capability goals, including sponsorship, owners, audience, outcomes, cohorts and connected delivery surfaces.",
        group: "Programs",
        icon: "book",
      },
      {
        id: "learning-tracks",
        title: "Role-Based Learning Tracks",
        description:
          "Map job roles and capability goals to required modules, Classes, Labs, Build Rooms, projects, assessments and credential evidence.",
        group: "Learning Design",
        icon: "path",
      },
      {
        id: "learning-cohorts",
        title: "Cohorts & Enrolment Windows",
        description:
          "Create and monitor exact cohorts, dates, schedules, time zones, languages, delivery modes, capacity, instructors and enrolment readiness.",
        group: "Delivery",
        icon: "users",
        badge: "4",
      },
      {
        id: "learning-assignments",
        title: "Assignments & Delivery Work",
        description:
          "Coordinate organization-linked assignments, requirements, owners, deadlines, Build Rooms, intended Labs, reviews and exceptions.",
        group: "Learning Delivery",
        icon: "clipboard",
      },
      {
        id: "learning-assessments",
        title: "Assessment & Moderation Oversight",
        description:
          "Monitor assessment throughput, accommodations, moderation, integrity handoffs and publication readiness without changing academic decisions.",
        group: "Assessment",
        icon: "gavel",
      },
      {
        id: "learning-calendar",
        title: "Learning Calendar",
        description:
          "Plan cohort dates, live Classes, Labs windows, assignment deadlines, project reviews, assessments and organization milestones together.",
        group: "Delivery",
        icon: "calendar",
      },
    ],
  },
  {
    id: "class",
    label: "Connected Chatoner Class",
    shortLabel: "Class",
    icon: "video",
    accent: "purple",
    routes: [
      {
        id: "class-connection",
        title: "Chatoner Class Connection",
        description:
          "Prepare secure one-time handoffs into class.chatoner.com while preserving independent Class authorization, media, captions and attendance ownership.",
        group: "Connected Class",
        icon: "link",
      },
      {
        id: "class-sessions",
        title: "Live Class Session Register",
        description:
          "Review upcoming and active Class sessions, unique codes, instructors, cohorts, observer scope, readiness and connection state.",
        group: "Connected Class",
        icon: "video",
        badge: "3",
      },
      {
        id: "class-attendance",
        title: "Attendance & Participation Sync",
        description:
          "Reconcile aggregate attendance, late arrivals, excused absences and support follow-up without exposing private live participation.",
        group: "Class Operations",
        icon: "check",
      },
      {
        id: "class-recordings",
        title: "Recordings, Captions & Access",
        description:
          "Manage authorized recording availability, captions, transcripts, languages, retention, legal hold and role-scoped access.",
        group: "Class Records",
        icon: "play",
      },
      {
        id: "class-office-hours",
        title: "Office Hours & Sponsored Clinics",
        description:
          "Coordinate mentor office hours, team clinics, project reviews and smaller-group huddles with purpose, capacity and Class handoff.",
        group: "Class Support",
        icon: "clock",
      },
    ],
  },
  {
    id: "rooms",
    label: "Build Rooms",
    shortLabel: "Rooms",
    icon: "layout",
    accent: "blue",
    routes: [
      {
        id: "rooms-portfolio",
        title: "Build Room Portfolio",
        description:
          "Monitor instructor-owned Build Rooms by program, cohort, project, progress, approved evidence and human checkpoint.",
        group: "Build Rooms",
        icon: "layout",
      },
      {
        id: "rooms-room",
        title: "Kijani Build Room Command",
        description:
          "Inspect requirements, aggregate progress, approved artifacts, decisions and evidence without reading protected learner conversations.",
        group: "Build Rooms",
        icon: "message",
      },
      {
        id: "rooms-groups",
        title: "Smaller-Group Oversight",
        description:
          "View group structure, leads, intended Labs, huddle codes, branches, progress and privacy-safe aggregate evidence while preserving instructor authority.",
        group: "Smaller Groups",
        icon: "network",
      },
      {
        id: "rooms-evidence",
        title: "Build Room Evidence Review",
        description:
          "Review organization-visible Skill, System and Proof evidence, requirement links, human decisions and unresolved conditions.",
        group: "Evidence",
        icon: "award",
      },
      {
        id: "rooms-permissions",
        title: "Room Permission Boundaries",
        description:
          "Define organization, instructor, learner, reviewer and AI-agent visibility, actions, data scopes, purpose, expiry and emergency revocation.",
        group: "Room Governance",
        icon: "lock",
      },
    ],
  },
  {
    id: "labs",
    label: "Connected AI Labs",
    shortLabel: "Labs",
    icon: "flask",
    accent: "aqua",
    routes: [
      {
        id: "labs-connection",
        title: "Chatoner AI Labs Connection",
        description:
          "Coordinate secure one-time handoffs to labs.chatoner.com while Labs independently owns practical runtime, tools, compute, safety and Lab-native evidence.",
        group: "Connected Labs",
        icon: "link",
      },
      {
        id: "labs-catalogue",
        title: "Eligible & Intended Labs",
        description:
          "Review Labs approved for organizational programs, roles, assignments and outcomes with exact environment, prerequisites and visibility boundaries.",
        group: "Lab Catalogue",
        icon: "flask",
      },
      {
        id: "labs-entitlements",
        title: "Lab Entitlements & Capacity",
        description:
          "Manage seats, time, token, compute and environment entitlements by program, cohort and learner while enforcing sponsorship conditions.",
        group: "Lab Administration",
        icon: "key",
      },
      {
        id: "labs-runs",
        title: "Synced Lab Runs",
        description:
          "Review authorized run status, environment, learner eligibility, duration, policy state and return readiness without entering learner experiments.",
        group: "Lab Operations",
        icon: "pulse",
      },
      {
        id: "labs-evidence",
        title: "Returned Lab Evidence",
        description:
          "Reconcile signed evidence returned by Labs to assignment, learner, project and credential requirements with consent and human review.",
        group: "Lab Evidence",
        icon: "award",
      },
      {
        id: "labs-analytics",
        title: "Lab Adoption & Usage Analytics",
        description:
          "Analyze privacy-thresholded Lab adoption, session completion, entitlement utilization, cost, support needs and outcome relationships.",
        group: "Lab Analytics",
        icon: "chart",
      },
    ],
  },
  {
    id: "projects",
    label: "Projects & Credentials",
    shortLabel: "Projects",
    icon: "folder",
    accent: "orange",
    routes: [
      {
        id: "projects-portfolio",
        title: "Project Portfolio",
        description:
          "Manage workflow projects, capstones and controlled pilots by owner, stage, risk, modeled value, evidence and human decision.",
        group: "Projects",
        icon: "folder",
      },
      {
        id: "projects-workflow-projects",
        title: "Workflow Project Delivery",
        description:
          "Move organization AI workflow projects through discover, design, build, pilot, approve, deploy, adopt and measure with traceability.",
        group: "Projects",
        icon: "workflow",
      },
      {
        id: "projects-capstones",
        title: "Capstones & Review Panels",
        description:
          "Coordinate capstone readiness, presentation panels, evaluator scope, evidence, moderation, outcome and appeal boundaries.",
        group: "Capstones",
        icon: "trophy",
      },
      {
        id: "projects-credentials",
        title: "Credential Readiness",
        description:
          "Track recommendation, evidence completeness, assessment state, human approval, issuance, verification and revocation without issuing credentials directly.",
        group: "Credentials",
        icon: "certificate",
      },
      {
        id: "projects-evidence",
        title: "Skill, System & Proof Centre",
        description:
          "Connect capability claims to learner contribution, system artifacts, tests, AI assistance, human decisions and observable outcomes.",
        group: "Evidence",
        icon: "award",
      },
    ],
  },
  {
    id: "governance",
    label: "Governance & Risk",
    shortLabel: "Governance",
    icon: "shield",
    accent: "red",
    routes: [
      {
        id: "governance-overview",
        title: "Governance Command",
        description:
          "Monitor policy maturity, approved tools, risk, acknowledgements, incidents, audit, human authority and cross-platform control coverage.",
        group: "Governance",
        icon: "shield",
      },
      {
        id: "governance-policies",
        title: "Policy Lifecycle",
        description:
          "Manage policy drafting, review, approval, publication, acknowledgement, exception, review date and retirement with version history.",
        group: "Policies",
        icon: "file",
      },
      {
        id: "governance-approved-tools",
        title: "Approved AI Tool Catalogue",
        description:
          "Review approved tools, models, vendors, intended purposes, data classes, conditions, owners, expiry and prohibited uses.",
        group: "Approved Tools",
        icon: "bot",
      },
      {
        id: "governance-risk-register",
        title: "AI Risk Register",
        description:
          "Capture risk statements, affected processes, likelihood, impact, controls, treatment owners, due dates and accepted residual risk.",
        group: "Risk",
        icon: "alert",
        badge: "6",
      },
      {
        id: "governance-acknowledgements",
        title: "Policy Acknowledgements",
        description:
          "Track assigned acknowledgements, reminders, completion, exceptions, role changes and evidence by policy version and audience.",
        group: "Policy Adoption",
        icon: "check",
      },
      {
        id: "governance-audit",
        title: "Governance Audit Trail",
        description:
          "Inspect attributable policy, tool, risk, access, approval and emergency-control events with correlation and evidence references.",
        group: "Audit",
        icon: "history",
      },
    ],
  },
  {
    id: "integrations",
    label: "Integrations",
    shortLabel: "Integrations",
    icon: "link",
    accent: "cyan",
    routes: [
      {
        id: "integrations-catalogue",
        title: "Integration Catalogue",
        description:
          "Manage identity, HRIS, LMS, calendar, collaboration, finance and workflow connectors with owners, permissions, health and data scope.",
        group: "Integration Portfolio",
        icon: "link",
      },
      {
        id: "integrations-identity",
        title: "Identity & HRIS Integration",
        description:
          "Map identities, employment status, teams and allowed attributes into Academy membership without importing unrelated HR data.",
        group: "Identity Integration",
        icon: "key",
      },
      {
        id: "integrations-learning",
        title: "LMS & Learning Data Integration",
        description:
          "Map programs, cohorts, assignments, completion and approved evidence between Academy and an organization-approved LMS.",
        group: "Learning Integration",
        icon: "book",
      },
      {
        id: "integrations-collaboration",
        title: "Collaboration & Calendar Integrations",
        description:
          "Configure organization-approved collaboration and calendar tools, delivery channels, scopes, consent, rate limits and health.",
        group: "Collaboration Integration",
        icon: "message",
      },
      {
        id: "integrations-automation",
        title: "Automation & Webhooks",
        description:
          "Create signed event subscriptions, transformations, retries, replay, dead-letter handling, secrets and destination-specific scopes.",
        group: "Automation",
        icon: "code",
      },
      {
        id: "integrations-health",
        title: "Integration Health & Diagnostics",
        description:
          "Monitor authentication, authorization, latency, schema compatibility, event lag, webhook delivery, retries, errors and recovery actions.",
        group: "Integration Operations",
        icon: "pulse",
      },
    ],
  },
  {
    id: "procurement",
    label: "Procurement",
    shortLabel: "Procurement",
    icon: "cart",
    accent: "orange",
    routes: [
      {
        id: "procurement-overview",
        title: "Procurement Command",
        description:
          "Monitor vendor due diligence, contracts, data protection, statements of work, purchase orders, approvals and renewal dependencies.",
        group: "Procurement",
        icon: "cart",
      },
      {
        id: "procurement-documents",
        title: "Vendor & Security Documents",
        description:
          "Organize security evidence, insurance, tax, privacy, accessibility, responsible AI and vendor documents with expiry and review state.",
        group: "Due Diligence",
        icon: "file",
      },
      {
        id: "procurement-contracts",
        title: "Contracts, DPA & SOW",
        description:
          "Manage contract, DPA and SOW drafting, review, negotiation, signatures, obligations, milestones, amendments and termination.",
        group: "Commercial Records",
        icon: "briefcase",
      },
      {
        id: "procurement-purchase-orders",
        title: "Purchase Orders",
        description:
          "Track approved purchase orders, line items, budget, invoice matching, remaining commitment, owner and delivery status.",
        group: "Purchasing",
        icon: "receipt",
      },
      {
        id: "procurement-approvals",
        title: "Procurement Approval Workflow",
        description:
          "Route procurement requests through budget, security, privacy, legal, governance and executive approvals with segregation of duties.",
        group: "Approvals",
        icon: "check",
        badge: "7",
      },
    ],
  },
  {
    id: "finance",
    label: "Billing & Finance",
    shortLabel: "Finance",
    icon: "wallet",
    accent: "green",
    routes: [
      {
        id: "finance-overview",
        title: "Finance Command",
        description:
          "Review plan value, invoiced amount, paid amount, outstanding liabilities, credits, usage, sponsorship and renewal assumptions.",
        group: "Finance",
        icon: "wallet",
      },
      {
        id: "finance-plan",
        title: "Plan, Seats & Commercial Terms",
        description:
          "Model contracted seats, assigned seats, program access, support, data region, term, unit pricing and approved plan changes.",
        group: "Plan",
        icon: "users",
      },
      {
        id: "finance-invoices",
        title: "Invoices, Payments & Credits",
        description:
          "Track invoice state, amount, currency, payment, credit note, dispute, purchase-order match and reconciliation evidence.",
        group: "Receivables",
        icon: "receipt",
        badge: "3",
      },
      {
        id: "finance-usage",
        title: "Usage & Charge Attribution",
        description:
          "Analyze seats, Class, Labs, storage, support and optional service usage with allocation, thresholds and billing assumptions.",
        group: "Usage",
        icon: "chart",
      },
      {
        id: "finance-renewal",
        title: "Renewal Planning",
        description:
          "Prepare renewal decisions using realized value, adoption, usage, support, risk, capacity, terms, budget and named approval.",
        group: "Renewal",
        icon: "calendar",
      },
    ],
  },
  {
    id: "reports",
    label: "Reports & Exports",
    shortLabel: "Reports",
    icon: "chart",
    accent: "violet",
    routes: [
      {
        id: "reports-library",
        title: "Report Library",
        description:
          "Browse scheduled and generated executive, learning, adoption, governance, finance, support and audit reports with classification and access.",
        group: "Reports",
        icon: "library",
      },
      {
        id: "reports-executive",
        title: "Executive Capability Report",
        description:
          "Generate a leadership report combining readiness, adoption, learning, projects, governance, finance and evidence-backed value.",
        group: "Executive Reporting",
        icon: "chart",
      },
      {
        id: "reports-learning",
        title: "Learning & Credential Report",
        description:
          "Report enrolment, participation, completion, support, assessment and credential readiness using privacy thresholds and role scope.",
        group: "Learning Reporting",
        icon: "book",
      },
      {
        id: "reports-adoption",
        title: "Adoption & Barrier Report",
        description:
          "Compare team adoption, barriers, interventions, support actions and change over time without ranking individual learners.",
        group: "Adoption Reporting",
        icon: "pulse",
      },
      {
        id: "reports-governance",
        title: "Governance & Risk Report",
        description:
          "Report policy status, approved tools, risk treatment, acknowledgements, access reviews, incidents and audit evidence.",
        group: "Governance Reporting",
        icon: "shield",
      },
      {
        id: "reports-custom",
        title: "Custom Report Builder",
        description:
          "Select authorized measures, filters, date ranges, language, classification, recipients, schedule and human approval for a custom export.",
        group: "Report Builder",
        icon: "sliders",
      },
    ],
  },
  {
    id: "support",
    label: "Support & Success",
    shortLabel: "Support",
    icon: "help",
    accent: "blue",
    routes: [
      {
        id: "support-success-plan",
        title: "Organization Success Plan",
        description:
          "Manage agreed outcomes, milestones, adoption interventions, executive checkpoints, owners, due dates and evidence of success.",
        group: "Success",
        icon: "target",
      },
      {
        id: "support-tickets",
        title: "Support Tickets & Escalations",
        description:
          "Track technical, learning, accessibility, finance, governance and integration requests with severity, SLA, owner and resolution.",
        group: "Support",
        icon: "inbox",
        badge: "5",
      },
      {
        id: "support-contacts",
        title: "Success Team & Contacts",
        description:
          "Maintain organization contacts and assigned Academy success, support, finance, governance, technical and executive contacts.",
        group: "People",
        icon: "users",
      },
      {
        id: "support-resources",
        title: "Organization Resource Library",
        description:
          "Organize approved playbooks, policies, templates, recordings, guides, FAQs and adoption materials by audience and language.",
        group: "Resources",
        icon: "folder",
      },
      {
        id: "support-service-status",
        title: "Service Status & Maintenance",
        description:
          "Monitor Academy, Class, Labs, handoff, notification and integration status, maintenance, incidents and resolved service history.",
        group: "Service Health",
        icon: "pulse",
      },
    ],
  },
  {
    id: "search",
    label: "Academy Search",
    shortLabel: "Search",
    icon: "search",
    accent: "cyan",
    routes: [
      {
        id: "search-global",
        title: "Organization Universal Search",
        description:
          "Search authorized people, programs, cohorts, Classes, Labs, Build Rooms, projects, policies, procurement, finance, reports and support records.",
        group: "Search",
        icon: "search",
      },
    ],
  },
  {
    id: "notifications",
    label: "Notifications",
    shortLabel: "Alerts",
    icon: "bell",
    accent: "orange",
    routes: [
      {
        id: "notifications-inbox",
        title: "Organization Notification Centre",
        description:
          "Review actionable alerts from learning, Class, Labs, Rooms, projects, governance, finance, procurement, integrations and support.",
        group: "Notifications",
        icon: "bell",
        badge: "14",
      },
    ],
  },
  {
    id: "settings",
    label: "Organization Settings",
    shortLabel: "Settings",
    icon: "settings",
    accent: "slate",
    routes: [
      {
        id: "settings-profile",
        title: "Organization Profile",
        description:
          "Configure organization identity, legal name, addresses, primary contacts, sector, countries, time zones and Academy operating context.",
        group: "Organization",
        icon: "building",
      },
      {
        id: "settings-preferences",
        title: "Workspace Preferences",
        description:
          "Configure default languages, time zones, date formats, notification channels, quiet hours, accessibility and report preferences.",
        group: "Experience",
        icon: "sliders",
      },
      {
        id: "settings-privacy",
        title: "Privacy, Retention & Data Rights",
        description:
          "Configure approved data purposes, retention, deletion, legal hold, subject rights, export controls and cross-platform data boundaries.",
        group: "Privacy",
        icon: "shield",
      },
      {
        id: "settings-branding",
        title: "Organization Branding",
        description:
          "Configure approved organization name, logo reference, accent, email and report branding without replacing Chatoner product identity.",
        group: "Experience",
        icon: "sparkles",
      },
      {
        id: "settings-security",
        title: "Security & Sessions",
        description:
          "Configure MFA, SSO enforcement, session duration, allowed domains, privileged actions, emergency access and security notifications.",
        group: "Security",
        icon: "lock",
      },
      {
        id: "settings-audit",
        title: "Settings Audit",
        description:
          "Inspect attributable profile, privacy, security, branding, language, notification and integration configuration changes.",
        group: "Settings Governance",
        icon: "history",
      },
    ],
  },
];

export const APP_DATA: Record<string, any> = {
  notifications: [
    {
      id: "n-1",
      title: "Instructor condition requires action",
      body: "Kijani staging verification must be attached before final learner merge.",
      time: "8 min",
      unread: true,
      area: "rooms",
      page: "rooms-evidence",
    },
    {
      id: "n-2",
      title: "October cohort reaches 86% capacity",
      body: "Review remaining seats, waitlist and instructor coverage.",
      time: "24 min",
      unread: true,
      area: "learning",
      page: "learning-cohorts",
    },
    {
      id: "n-3",
      title: "High-risk AI tool review due",
      body: "The browser extension request requires Governance and Technical review.",
      time: "1 hr",
      unread: true,
      area: "governance",
      page: "governance-approved-tools",
    },
    {
      id: "n-4",
      title: "SCIM reconciliation recovered",
      body: "Four member mismatches were resolved and the audit record is available.",
      time: "Yesterday",
      unread: false,
      area: "integrations",
      page: "integrations-health",
    },
    {
      id: "n-5",
      title: "Invoice matched to purchase order",
      body: "INV-CAA-2026-1042 is reconciled against PO-2026-041.",
      time: "Yesterday",
      unread: false,
      area: "finance",
      page: "finance-invoices",
    },
  ],
};

export const LANGUAGES = LANGUAGE_OPTIONS.map(({ value, code }) => [value, code] as const);

export const PAGE_SPECS: Record<string, WorkspaceSpec> = {
  "command-dashboard": {
    id: "command-dashboard",
    area: "command",
    areaLabel: "Organization Command",
    title: "Executive Capability Command",
    description:
      "A unified executive view of capability, learning delivery, adoption, governed AI use, commercial exposure and accountable next actions.",
    group: "Executive Command",
    icon: "home",
    layout: "dashboard",
    model: "Executive Capability Command Centre",
    primaryAction: "Open action centre",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Active learners", value: "68%", trend: "Within approved threshold", icon: "home" },
      { label: "Capability readiness", value: "14", trend: "Human review required", icon: "chart" },
      { label: "Responsible adoption", value: "76%", trend: "Updated today", icon: "check" },
      { label: "Priority actions", value: "12", trend: "On target", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Priority / Domain" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "status", label: "Status" },
      { key: "updated", label: "Updated" },
    ],
    rows: [
      {
        id: "COMMAND-DASHBOARD-01",
        name: "AI capability operating review",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Executive Command",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "COMMAND-DASHBOARD-02",
        name: "October cohort delivery health",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "COMMAND-DASHBOARD-03",
        name: "Responsible AI adoption intervention",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "COMMAND-DASHBOARD-04",
        name: "2027 Academy renewal decision",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature: "organization::command::dashboard::executive-capability-command-centre::v2",
  },
  "command-readiness": {
    id: "command-readiness",
    area: "command",
    areaLabel: "Organization Command",
    title: "AI Readiness Diagnostic",
    description:
      "Measure strategy, leadership, people, process, data, technology and governance readiness with evidence, owners and dated actions.",
    group: "Capability & Readiness",
    icon: "pulse",
    layout: "scorecard",
    model: "AI Readiness Diagnostic Engine",
    primaryAction: "Start reassessment",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Leadership", value: "68%", trend: "On target", icon: "pulse" },
      { label: "People & skills", value: "14", trend: "1 exception", icon: "chart" },
      { label: "Process & data", value: "76%", trend: "+8% this cycle", icon: "check" },
      { label: "Governance", value: "12", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Domain" },
      { key: "value", label: "Current" },
      { key: "target", label: "Target" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "COMMAND-READINESS-01",
        name: "Leadership sponsorship",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Capability & Readiness",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "COMMAND-READINESS-02",
        name: "Workforce capability",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "COMMAND-READINESS-03",
        name: "Process readiness",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "COMMAND-READINESS-04",
        name: "Data and integration readiness",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "COMMAND-READINESS-05",
        name: "Governance and human authority",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature: "organization::command::readiness::ai-readiness-diagnostic-engine::v2",
  },
  "command-adoption": {
    id: "command-adoption",
    area: "command",
    areaLabel: "Organization Command",
    title: "Adoption & Barrier Register",
    description:
      "Compare responsible AI adoption across teams and track barriers, interventions, owners, due dates and verified change.",
    group: "Adoption",
    icon: "chart",
    layout: "matrix",
    model: "Team Adoption and Barrier Matrix",
    primaryAction: "Add barrier",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Teams measured", value: "5", trend: "+8% this cycle", icon: "chart" },
      { label: "Adoption rate", value: "92%", trend: "3 actions due", icon: "chart" },
      { label: "Open barriers", value: "268", trend: "Evidence current", icon: "check" },
      { label: "Interventions due", value: "83%", trend: "Within approved threshold", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Subject" },
      { key: "scope", label: "Scope" },
      { key: "level", label: "Level" },
      { key: "progress", label: "Coverage" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "COMMAND-ADOPTION-01",
        name: "Operations",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Adoption",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "COMMAND-ADOPTION-02",
        name: "Customer Experience",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "COMMAND-ADOPTION-03",
        name: "Finance & Procurement",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "COMMAND-ADOPTION-04",
        name: "Technology & Data",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "COMMAND-ADOPTION-05",
        name: "People & Culture",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Attention",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::command::adoption::team-adoption-and-barrier-matrix::v2",
  },
  "command-outcomes": {
    id: "command-outcomes",
    area: "command",
    areaLabel: "Organization Command",
    title: "Outcomes & Value Studio",
    description:
      "Separate expected, modeled and realized value while preserving assumptions, baselines, evidence sources and human approval.",
    group: "Outcomes & Value",
    icon: "target",
    layout: "analytics",
    model: "Outcome and Value Attribution Studio",
    primaryAction: "Generate value review",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Modeled value", value: "5", trend: "Human review required", icon: "target" },
      { label: "Realized evidence", value: "92%", trend: "Updated today", icon: "chart" },
      { label: "Outcome owners", value: "268", trend: "On target", icon: "check" },
      { label: "Claims awaiting review", value: "83%", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Measure" },
      { key: "value", label: "Value" },
      { key: "trend", label: "Trend" },
      { key: "source", label: "Evidence source" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "COMMAND-OUTCOMES-01",
        name: "Reduced customer response time",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Outcomes & Value",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "COMMAND-OUTCOMES-02",
        name: "Faster procurement evidence review",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "COMMAND-OUTCOMES-03",
        name: "Improved workflow quality",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "COMMAND-OUTCOMES-04",
        name: "Higher learner confidence",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "COMMAND-OUTCOMES-05",
        name: "Controlled AI tool adoption",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Attention",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::command::outcomes::outcome-and-value-attribution-studio::v2",
  },
  "command-action-centre": {
    id: "command-action-centre",
    area: "command",
    areaLabel: "Organization Command",
    title: "Cross-Organization Action Centre",
    description:
      "Route high-priority work from people, learning, Class, Labs, Build Rooms, governance, finance, integrations and support to named owners.",
    group: "Executive Command",
    icon: "bolt",
    layout: "queue",
    model: "Cross-Organization Priority Action Centre",
    primaryAction: "Create action",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Urgent", value: "USD 720K", trend: "3 actions due", icon: "bolt" },
      { label: "Review due", value: "231", trend: "Evidence current", icon: "chart" },
      { label: "Cross-surface", value: "UGX 184M", trend: "Within approved threshold", icon: "check" },
      { label: "Ready to close", value: "4", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "deadline", label: "Due" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "COMMAND-ACTION-CENTRE-01",
        name: "Approve October cohort capacity",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Urgent",
        progress: 75,
        people: 39,
        updated: "Today 09:22",
        role: "Owner",
        value: "USD 720K",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "75%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Executive Command",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "COMMAND-ACTION-CENTRE-02",
        name: "Resolve SCIM reconciliation exception",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Review due",
        progress: 86,
        people: 56,
        updated: "Today 08:40",
        role: "Manager",
        value: "268",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "86%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "COMMAND-ACTION-CENTRE-03",
        name: "Review high-risk AI tool request",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 54,
        people: 73,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "76%",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "54%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "COMMAND-ACTION-CENTRE-04",
        name: "Confirm Kijani staging evidence",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "Ready",
        progress: 65,
        people: 90,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "231",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "65%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "COMMAND-ACTION-CENTRE-05",
        name: "Approve renewal planning assumptions",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Urgent",
        progress: 76,
        people: 107,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "83%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "76%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [67, 74, 81, 88, 47, 54, 61, 68],
    external: null,
    signature: "organization::command::action-centre::cross-organization-priority-action-centre::v2",
  },
  "people-members": {
    id: "people-members",
    area: "people",
    areaLabel: "People & Access",
    title: "Members, Seats & Invitations",
    description:
      "Manage organization members, Academy seats, invitation state, team placement, role scope and account lifecycle without exposing unrelated HR records.",
    group: "Membership",
    icon: "users",
    layout: "directory",
    model: "Organization Membership and Seat Directory",
    primaryAction: "Invite member",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Members", value: "12", trend: "Evidence current", icon: "users" },
      { label: "Assigned seats", value: "68%", trend: "Within approved threshold", icon: "chart" },
      { label: "Invitations", value: "14", trend: "Human review required", icon: "check" },
      { label: "Privileged roles", value: "76%", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "role", label: "Role / Type" },
      { key: "scope", label: "Team / Scope" },
      { key: "status", label: "Status" },
      { key: "updated", label: "Last activity" },
    ],
    rows: [
      {
        id: "PEOPLE-MEMBERS-01",
        name: "Ronald Isabirye",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Pending",
        progress: 68,
        people: 32,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "68%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Membership",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PEOPLE-MEMBERS-02",
        name: "Amina Mutesi",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 79,
        people: 49,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "79%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PEOPLE-MEMBERS-03",
        name: "Grace Namuli",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "On track",
        progress: 90,
        people: 66,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "90%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PEOPLE-MEMBERS-04",
        name: "David Kato",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 58,
        people: 83,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "58%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PEOPLE-MEMBERS-05",
        name: "Sarah Atim",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Active",
        progress: 69,
        people: 100,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "69%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
      {
        id: "PEOPLE-MEMBERS-06",
        name: "Peter Ouma",
        owner: "Mariam Nansubuga",
        scope: "Organization-wide",
        status: "Ready",
        progress: 80,
        people: 117,
        updated: "Today 09:22",
        role: "Owner",
        value: "92%",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "80%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Membership",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [60, 67, 74, 81, 88, 47, 54, 61],
    external: null,
    signature: "organization::people::members::organization-membership-and-seat-directory::v2",
  },
  "people-teams": {
    id: "people-teams",
    area: "people",
    areaLabel: "People & Access",
    title: "Teams & Departments",
    description:
      "Model teams, departments, leaders, membership, learning sponsorship, adoption priorities and delegated reporting boundaries.",
    group: "Organization Structure",
    icon: "network",
    layout: "hierarchy",
    model: "Team and Department Operating Hierarchy",
    primaryAction: "Create team",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Teams", value: "—", trend: "Not reported", icon: "network" },
      { label: "People covered", value: "—", trend: "Not reported", icon: "chart" },
      { label: "Learners sponsored", value: "—", trend: "Not reported", icon: "check" },
      { label: "Leaders assigned", value: "—", trend: "Not reported", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Team / Entity" },
      { key: "owner", label: "Lead" },
      { key: "people", label: "People" },
      { key: "progress", label: "Readiness" },
      { key: "status", label: "Status" },
    ],
    rows: [],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [],
    external: null,
    signature: "organization::people::teams::team-and-department-operating-hierarchy::v2",
  },
  "people-learners": {
    id: "people-learners",
    area: "people",
    areaLabel: "People & Access",
    title: "Sponsored Learners",
    description:
      "Track sponsored learners from eligibility and invitation through cohort enrolment, participation, support signals, completion and offboarding.",
    group: "Learning Population",
    icon: "student",
    layout: "onboarding",
    model: "Sponsored Learner Lifecycle and Support Queue",
    primaryAction: "Enrol learners",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Sponsored learners", value: "UGX 184M", trend: "Updated today", icon: "student" },
      { label: "Active", value: "4", trend: "On target", icon: "chart" },
      { label: "Support signals", value: "USD 720K", trend: "1 exception", icon: "check" },
      { label: "Completion-ready", value: "231", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Journey / Cohort" },
      { key: "owner", label: "Owner" },
      { key: "progress", label: "Progress" },
      { key: "deadline", label: "Next date" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PEOPLE-LEARNERS-01",
        name: "Mary Nakanwagi",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Healthy",
        progress: 69,
        people: 33,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "69%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Learning Population",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PEOPLE-LEARNERS-02",
        name: "Samuel Kato",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 80,
        people: 50,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "80%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PEOPLE-LEARNERS-03",
        name: "Aisha Nambasa",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "Review due",
        progress: 91,
        people: 67,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "91%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PEOPLE-LEARNERS-04",
        name: "Daniel Ouma",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Active",
        progress: 59,
        people: 84,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "59%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PEOPLE-LEARNERS-05",
        name: "Faith Atim",
        owner: "Mariam Nansubuga",
        scope: "Executive committee",
        status: "Ready",
        progress: 70,
        people: 101,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "92%",
        target: "85%",
        level: "Optimized",
        trend: "Within approved threshold",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "70%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [61, 68, 75, 82, 89, 48, 55, 62],
    external: null,
    signature: "organization::people::learners::sponsored-learner-lifecycle-and-support-queue::v2",
  },
  "people-instructors": {
    id: "people-instructors",
    area: "people",
    areaLabel: "People & Access",
    title: "Instructors, Mentors & Assessors",
    description:
      "Coordinate assigned instructors, mentors and assessors, teaching workload, cohort coverage, Class access, Labs scope and review capacity.",
    group: "Teaching Capacity",
    icon: "teacher",
    layout: "capacity",
    model: "Instructor and Mentor Capacity Planner",
    primaryAction: "Invite instructor",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Teaching roles", value: "4", trend: "On target", icon: "teacher" },
      { label: "Cohort coverage", value: "USD 720K", trend: "1 exception", icon: "chart" },
      { label: "Reviews due", value: "231", trend: "+8% this cycle", icon: "check" },
      { label: "Capacity available", value: "UGX 184M", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Resource" },
      { key: "capacity", label: "Capacity" },
      { key: "usage", label: "Assigned / Used" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PEOPLE-INSTRUCTORS-01",
        name: "Amina Mutesi",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Active",
        progress: 72,
        people: 36,
        updated: "Today 09:22",
        role: "Owner",
        value: "4",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "72%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Teaching Capacity",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PEOPLE-INSTRUCTORS-02",
        name: "Grace Namuli",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 83,
        people: 53,
        updated: "Today 08:40",
        role: "Manager",
        value: "92%",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "83%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PEOPLE-INSTRUCTORS-03",
        name: "Daniel Mwesigwa",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Attention",
        progress: 94,
        people: 70,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "14",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "94%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PEOPLE-INSTRUCTORS-04",
        name: "John Nsubuga",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Approved",
        progress: 62,
        people: 87,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "USD 720K",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "62%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PEOPLE-INSTRUCTORS-05",
        name: "Sarah Kato",
        owner: "Sarah Atim",
        scope: "Operations team",
        status: "Pending",
        progress: 73,
        people: 104,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "268",
        target: "85%",
        level: "Optimized",
        trend: "On target",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "73%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [64, 71, 78, 85, 92, 51, 58, 65],
    external: null,
    signature: "organization::people::instructors::instructor-and-mentor-capacity-planner::v2",
  },
  "people-roles": {
    id: "people-roles",
    area: "people",
    areaLabel: "People & Access",
    title: "Roles, Permissions & Delegation",
    description:
      "Administer least-privilege roles, scoped authority, segregation of duties, expiry, MFA requirements and approval boundaries.",
    group: "Access Governance",
    icon: "lock",
    layout: "matrix",
    model: "Organization Role and Permission Authority Matrix",
    primaryAction: "Create delegated role",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Organization roles", value: "231", trend: "3 actions due", icon: "lock" },
      { label: "Privileged roles", value: "UGX 184M", trend: "Evidence current", icon: "chart" },
      { label: "Expiring grants", value: "4", trend: "Within approved threshold", icon: "check" },
      { label: "Access reviews", value: "USD 720K", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Subject" },
      { key: "scope", label: "Scope" },
      { key: "level", label: "Level" },
      { key: "progress", label: "Coverage" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PEOPLE-ROLES-01",
        name: "Organization Owner",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 66,
        people: 30,
        updated: "Today 09:22",
        role: "Owner",
        value: "231",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "66%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Access Governance",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PEOPLE-ROLES-02",
        name: "Program Manager",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 77,
        people: 47,
        updated: "Today 08:40",
        role: "Manager",
        value: "83%",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "77%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PEOPLE-ROLES-03",
        name: "People Admin",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 88,
        people: 64,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "12",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "88%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PEOPLE-ROLES-04",
        name: "Governance Lead",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 56,
        people: 81,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "UGX 184M",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "56%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PEOPLE-ROLES-05",
        name: "Finance Admin",
        owner: "Ronald Isabirye",
        scope: "Finance & Procurement",
        status: "On track",
        progress: 67,
        people: 98,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "5",
        target: "85%",
        level: "Optimized",
        trend: "+8% this cycle",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "67%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
      {
        id: "PEOPLE-ROLES-06",
        name: "Technical Admin",
        owner: "Amina Mutesi",
        scope: "People & Culture",
        status: "Review due",
        progress: 78,
        people: 115,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "78%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Access Governance",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [58, 65, 72, 79, 86, 93, 52, 59],
    external: null,
    signature: "organization::people::roles::organization-role-and-permission-authority-matrix::v2",
    accessRoles: ["owner", "people-admin", "governance-lead", "technical-admin", "auditor"],
    manageRoles: ["owner", "people-admin"],
    approveRoles: ["owner"],
  },
  "people-access": {
    id: "people-access",
    area: "people",
    areaLabel: "People & Access",
    title: "SSO, SCIM & Access Reviews",
    description:
      "Connect SSO and SCIM, map attributes, reconcile memberships, certify privileged access, revoke stale sessions and preserve access evidence.",
    group: "Identity Operations",
    icon: "key",
    layout: "import",
    model: "Identity Provisioning and Access Certification Workflow",
    primaryAction: "Start access review",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Identity sources", value: "83%", trend: "1 exception", icon: "key" },
      { label: "Provisioned accounts", value: "5", trend: "+8% this cycle", icon: "chart" },
      { label: "Reconciliation issues", value: "92%", trend: "3 actions due", icon: "check" },
      { label: "Reviews due", value: "268", trend: "Evidence current", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Mapping / Job" },
      { key: "source", label: "Source" },
      { key: "scope", label: "Destination" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PEOPLE-ACCESS-01",
        name: "Microsoft Entra ID",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 67,
        people: 31,
        updated: "Today 09:22",
        role: "Owner",
        value: "83%",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "67%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Identity Operations",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PEOPLE-ACCESS-02",
        name: "SCIM member provisioning",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Pending",
        progress: 78,
        people: 48,
        updated: "Today 08:40",
        role: "Manager",
        value: "12",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "78%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PEOPLE-ACCESS-03",
        name: "Privileged-role access review",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Healthy",
        progress: 89,
        people: 65,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "UGX 184M",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "89%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PEOPLE-ACCESS-04",
        name: "Dormant-account revocation",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "On track",
        progress: 57,
        people: 82,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "5",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "57%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PEOPLE-ACCESS-05",
        name: "Domain and MFA policy",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Review due",
        progress: 68,
        people: 99,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "68%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "68%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [59, 66, 73, 80, 87, 46, 53, 60],
    external: null,
    signature: "organization::people::access::identity-provisioning-and-access-certification-workflow::v2",
    accessRoles: ["owner", "people-admin", "technical-admin", "governance-lead", "auditor"],
    manageRoles: ["owner", "people-admin", "technical-admin"],
    approveRoles: ["owner", "people-admin"],
  },
  "learning-programs": {
    id: "learning-programs",
    area: "learning",
    areaLabel: "Learning Operations",
    title: "Program Portfolio",
    description:
      "Manage Academy programs selected for organizational capability goals, including sponsorship, owners, audience, outcomes, cohorts and connected delivery surfaces.",
    group: "Programs",
    icon: "book",
    layout: "portfolio",
    model: "Organization Program Portfolio and Sponsorship Console",
    primaryAction: "Add program",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Programs", value: "68%", trend: "On target", icon: "book" },
      { label: "Active learners", value: "14", trend: "1 exception", icon: "chart" },
      { label: "Cohorts", value: "76%", trend: "+8% this cycle", icon: "check" },
      { label: "Outcome coverage", value: "12", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Portfolio item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Program / Team" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LEARNING-PROGRAMS-01",
        name: "AI Essentials for Everyday Life",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Programs",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LEARNING-PROGRAMS-02",
        name: "AI Product Builder",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LEARNING-PROGRAMS-03",
        name: "AI Automation & Workflow Systems",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LEARNING-PROGRAMS-04",
        name: "AI Governance & Safety Practice",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature: "organization::learning::programs::organization-program-portfolio-and-sponsorship-console::v2",
  },
  "learning-tracks": {
    id: "learning-tracks",
    area: "learning",
    areaLabel: "Learning Operations",
    title: "Role-Based Learning Tracks",
    description:
      "Map job roles and capability goals to required modules, Classes, Labs, Build Rooms, projects, assessments and credential evidence.",
    group: "Learning Design",
    icon: "path",
    layout: "map",
    model: "Role-Based Learning Track and Outcome Map",
    primaryAction: "Create track",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Role tracks", value: "UGX 184M", trend: "3 actions due", icon: "path" },
      { label: "Job families", value: "4", trend: "Evidence current", icon: "chart" },
      { label: "Required Labs", value: "USD 720K", trend: "Within approved threshold", icon: "check" },
      { label: "Evidence outcomes", value: "231", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Outcome / Node" },
      { key: "scope", label: "Connected to" },
      { key: "owner", label: "Owner" },
      { key: "evidence", label: "Evidence" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LEARNING-TRACKS-01",
        name: "Operations AI Practitioner",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Healthy",
        progress: 69,
        people: 33,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "69%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Learning Design",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LEARNING-TRACKS-02",
        name: "Customer Experience AI Lead",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 80,
        people: 50,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "80%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LEARNING-TRACKS-03",
        name: "Finance AI Control Owner",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "Review due",
        progress: 91,
        people: 67,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "91%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LEARNING-TRACKS-04",
        name: "Technical AI Builder",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Active",
        progress: 59,
        people: 84,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "59%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "LEARNING-TRACKS-05",
        name: "People & Culture AI Champion",
        owner: "Mariam Nansubuga",
        scope: "Executive committee",
        status: "Ready",
        progress: 70,
        people: 101,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "92%",
        target: "85%",
        level: "Optimized",
        trend: "Within approved threshold",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "70%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [61, 68, 75, 82, 89, 48, 55, 62],
    external: null,
    signature: "organization::learning::tracks::role-based-learning-track-and-outcome-map::v2",
  },
  "learning-cohorts": {
    id: "learning-cohorts",
    area: "learning",
    areaLabel: "Learning Operations",
    title: "Cohorts & Enrolment Windows",
    description:
      "Create and monitor exact cohorts, dates, schedules, time zones, languages, delivery modes, capacity, instructors and enrolment readiness.",
    group: "Delivery",
    icon: "users",
    layout: "onboarding",
    model: "Cohort Capacity and Enrolment Orchestrator",
    primaryAction: "Create cohort",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Open cohorts", value: "5", trend: "Evidence current", icon: "users" },
      { label: "Capacity", value: "92%", trend: "Within approved threshold", icon: "chart" },
      { label: "Confirmed learners", value: "268", trend: "Human review required", icon: "check" },
      { label: "Waitlisted", value: "83%", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Journey / Cohort" },
      { key: "owner", label: "Owner" },
      { key: "progress", label: "Progress" },
      { key: "deadline", label: "Next date" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LEARNING-COHORTS-01",
        name: "October 2026 Organization Cohort",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Delivery",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LEARNING-COHORTS-02",
        name: "January 2027 Leadership Cohort",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LEARNING-COHORTS-03",
        name: "February 2027 Builder Cohort",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LEARNING-COHORTS-04",
        name: "Private Governance Cohort",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::learning::cohorts::cohort-capacity-and-enrolment-orchestrator::v2",
  },
  "learning-assignments": {
    id: "learning-assignments",
    area: "learning",
    areaLabel: "Learning Operations",
    title: "Assignments & Delivery Work",
    description:
      "Coordinate organization-linked assignments, requirements, owners, deadlines, Build Rooms, intended Labs, reviews and exceptions.",
    group: "Learning Delivery",
    icon: "clipboard",
    layout: "board",
    model: "Organization Assignment Delivery Board",
    primaryAction: "Create assignment",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Assignments", value: "14", trend: "On target", icon: "clipboard" },
      { label: "In progress", value: "76%", trend: "1 exception", icon: "chart" },
      { label: "Review due", value: "12", trend: "+8% this cycle", icon: "check" },
      { label: "Blocked", value: "68%", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Requirement" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LEARNING-ASSIGNMENTS-01",
        name: "Accessible Kijani Checkout",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 74,
        people: 38,
        updated: "Today 09:22",
        role: "Owner",
        value: "14",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "74%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Learning Delivery",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LEARNING-ASSIGNMENTS-02",
        name: "Support Workflow Automation",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 85,
        people: 55,
        updated: "Today 08:40",
        role: "Manager",
        value: "USD 720K",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "85%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LEARNING-ASSIGNMENTS-03",
        name: "Governed Tool Assessment",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 96,
        people: 72,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "268",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "96%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LEARNING-ASSIGNMENTS-04",
        name: "Operational Pilot Outcome Story",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 64,
        people: 89,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "76%",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "64%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [66, 73, 80, 87, 46, 53, 60, 67],
    external: null,
    signature: "organization::learning::assignments::organization-assignment-delivery-board::v2",
  },
  "learning-assessments": {
    id: "learning-assessments",
    area: "learning",
    areaLabel: "Learning Operations",
    title: "Assessment & Moderation Oversight",
    description:
      "Monitor assessment throughput, accommodations, moderation, integrity handoffs and publication readiness without changing academic decisions.",
    group: "Assessment",
    icon: "gavel",
    layout: "rubric",
    model: "Assessment Moderation and Human Decision Console",
    primaryAction: "Open moderation cycle",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Assessments", value: "14", trend: "+8% this cycle", icon: "gavel" },
      { label: "Candidates", value: "76%", trend: "3 actions due", icon: "chart" },
      { label: "Moderation due", value: "12", trend: "Evidence current", icon: "check" },
      { label: "Publication-ready", value: "68%", trend: "Within approved threshold", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Assessment / Criterion" },
      { key: "owner", label: "Decision owner" },
      { key: "scope", label: "Cohort / Program" },
      { key: "progress", label: "Completion" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LEARNING-ASSESSMENTS-01",
        name: "AI Product Builder moderation",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 74,
        people: 38,
        updated: "Today 09:22",
        role: "Owner",
        value: "14",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "74%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Assessment",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LEARNING-ASSESSMENTS-02",
        name: "Governance practice review",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 85,
        people: 55,
        updated: "Today 08:40",
        role: "Manager",
        value: "USD 720K",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "85%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LEARNING-ASSESSMENTS-03",
        name: "Workflow system assessment",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 96,
        people: 72,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "268",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "96%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LEARNING-ASSESSMENTS-04",
        name: "Leadership readiness panel",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 64,
        people: 89,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "76%",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "64%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [66, 73, 80, 87, 46, 53, 60, 67],
    external: null,
    signature: "organization::learning::assessments::assessment-moderation-and-human-decision-console::v2",
  },
  "learning-calendar": {
    id: "learning-calendar",
    area: "learning",
    areaLabel: "Learning Operations",
    title: "Learning Calendar",
    description:
      "Plan cohort dates, live Classes, Labs windows, assignment deadlines, project reviews, assessments and organization milestones together.",
    group: "Delivery",
    icon: "calendar",
    layout: "calendar",
    model: "Organization Learning and Delivery Calendar",
    primaryAction: "Add calendar item",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Upcoming items", value: "68%", trend: "Within approved threshold", icon: "calendar" },
      { label: "Live Classes", value: "14", trend: "Human review required", icon: "chart" },
      { label: "Labs windows", value: "76%", trend: "Updated today", icon: "check" },
      { label: "Review panels", value: "12", trend: "On target", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Calendar item" },
      { key: "date", label: "Date" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Workspace" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LEARNING-CALENDAR-01",
        name: "Accessible Product Decisions",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Delivery",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LEARNING-CALENDAR-02",
        name: "AI-App Builder Lab window",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LEARNING-CALENDAR-03",
        name: "Kijani project checkpoint",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LEARNING-CALENDAR-04",
        name: "Assessment moderation board",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "LEARNING-CALENDAR-05",
        name: "Executive capability review",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: ["Event title", "Date", "Time", "Time zone", "Owner", "Connected workspace"],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature: "organization::learning::calendar::organization-learning-and-delivery-calendar::v2",
  },
  "class-connection": {
    id: "class-connection",
    area: "class",
    areaLabel: "Connected Chatoner Class",
    title: "Chatoner Class Connection",
    description:
      "Prepare secure one-time handoffs into class.chatoner.com while preserving independent Class authorization, media, captions and attendance ownership.",
    group: "Connected Class",
    icon: "link",
    layout: "connection",
    model: "Independent Chatoner Class Handoff Gateway",
    primaryAction: "Open Class workspace",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Class services", value: "5", trend: "Evidence current", icon: "link" },
      { label: "Handoff health", value: "92%", trend: "Within approved threshold", icon: "chart" },
      { label: "Authorized observers", value: "268", trend: "Human review required", icon: "check" },
      { label: "Return events", value: "83%", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Connected service" },
      { key: "provider", label: "Provider" },
      { key: "scope", label: "Data / authority scope" },
      { key: "updated", label: "Last check" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "CLASS-CONNECTION-01",
        name: "Organization Class workspace",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Connected Class",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "CLASS-CONNECTION-02",
        name: "Learner class handoff",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "CLASS-CONNECTION-03",
        name: "Authorized observer handoff",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "CLASS-CONNECTION-04",
        name: "Attendance return channel",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Connected service",
      "Client / tenant reference",
      "Authorized scopes",
      "Return URL",
      "Data owner",
      "Security review note",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: { domain: "class.chatoner.com", mode: "one-time signed organization handoff" },
    signature: "organization::class::connection::independent-chatoner-class-handoff-gateway::v2",
  },
  "class-sessions": {
    id: "class-sessions",
    area: "class",
    areaLabel: "Connected Chatoner Class",
    title: "Live Class Session Register",
    description:
      "Review upcoming and active Class sessions, unique codes, instructors, cohorts, observer scope, readiness and connection state.",
    group: "Connected Class",
    icon: "video",
    layout: "sessions",
    model: "Organization Live Class Session Register",
    primaryAction: "Create sponsored session",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Upcoming", value: "12", trend: "+8% this cycle", icon: "video" },
      { label: "Live now", value: "68%", trend: "3 actions due", icon: "chart" },
      { label: "Learners", value: "14", trend: "Evidence current", icon: "check" },
      { label: "Readiness issues", value: "76%", trend: "Within approved threshold", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Session / Run" },
      { key: "code", label: "Code / Reference" },
      { key: "owner", label: "Host / Owner" },
      { key: "date", label: "Schedule" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "CLASS-SESSIONS-01",
        name: "Accessible Product Decisions",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Pending",
        progress: 68,
        people: 32,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "68%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Connected Class",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "CLASS-SESSIONS-02",
        name: "Evaluation Clinic: Claims to Evidence",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 79,
        people: 49,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "79%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "CLASS-SESSIONS-03",
        name: "Kijani Project Review",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "On track",
        progress: 90,
        people: 66,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "90%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "CLASS-SESSIONS-04",
        name: "Governance Leadership Forum",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 58,
        people: 83,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "58%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [60, 67, 74, 81, 88, 47, 54, 61],
    external: { domain: "class.chatoner.com", mode: "one-time signed organization handoff" },
    signature: "organization::class::sessions::organization-live-class-session-register::v2",
  },
  "class-attendance": {
    id: "class-attendance",
    area: "class",
    areaLabel: "Connected Chatoner Class",
    title: "Attendance & Participation Sync",
    description:
      "Reconcile aggregate attendance, late arrivals, excused absences and support follow-up without exposing private live participation.",
    group: "Class Operations",
    icon: "check",
    layout: "matrix",
    model: "Privacy-Aware Attendance and Participation Matrix",
    primaryAction: "Reconcile attendance",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Attendance rate", value: "5", trend: "3 actions due", icon: "check" },
      { label: "Late arrivals", value: "92%", trend: "Evidence current", icon: "chart" },
      { label: "Excused", value: "268", trend: "Within approved threshold", icon: "check" },
      { label: "Follow-up needed", value: "83%", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Subject" },
      { key: "scope", label: "Scope" },
      { key: "level", label: "Level" },
      { key: "progress", label: "Coverage" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "CLASS-ATTENDANCE-01",
        name: "October Product Builder cohort",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Class Operations",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "CLASS-ATTENDANCE-02",
        name: "Operations workflow cohort",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "CLASS-ATTENDANCE-03",
        name: "Leadership readiness cohort",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "CLASS-ATTENDANCE-04",
        name: "Governance practice cohort",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: { domain: "class.chatoner.com", mode: "one-time signed organization handoff" },
    signature: "organization::class::attendance::privacy-aware-attendance-and-participation-matrix::v2",
  },
  "class-recordings": {
    id: "class-recordings",
    area: "class",
    areaLabel: "Connected Chatoner Class",
    title: "Recordings, Captions & Access",
    description:
      "Manage authorized recording availability, captions, transcripts, languages, retention, legal hold and role-scoped access.",
    group: "Class Records",
    icon: "play",
    layout: "media",
    model: "Class Recording, Caption and Retention Library",
    primaryAction: "Request recording access",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Authorized recordings", value: "5", trend: "On target", icon: "play" },
      { label: "Caption languages", value: "92%", trend: "1 exception", icon: "chart" },
      { label: "Expiring soon", value: "268", trend: "+8% this cycle", icon: "check" },
      { label: "Access requests", value: "83%", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Recording / Media" },
      { key: "duration", label: "Duration" },
      { key: "language", label: "Language" },
      { key: "scope", label: "Access" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "CLASS-RECORDINGS-01",
        name: "Accessible Product Decisions",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Class Records",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "CLASS-RECORDINGS-02",
        name: "Evaluating AI Without Guesswork",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "CLASS-RECORDINGS-03",
        name: "Designing Human Checkpoints",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "CLASS-RECORDINGS-04",
        name: "From Problem to Controlled Pilot",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: { domain: "class.chatoner.com", mode: "one-time signed organization handoff" },
    signature: "organization::class::recordings::class-recording,-caption-and-retention-library::v2",
  },
  "class-office-hours": {
    id: "class-office-hours",
    area: "class",
    areaLabel: "Connected Chatoner Class",
    title: "Office Hours & Sponsored Clinics",
    description:
      "Coordinate mentor office hours, team clinics, project reviews and smaller-group huddles with purpose, capacity and Class handoff.",
    group: "Class Support",
    icon: "clock",
    layout: "calendar",
    model: "Office Hour and Organization Clinic Scheduler",
    primaryAction: "Sponsor office hours",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Open clinics", value: "4", trend: "Updated today", icon: "clock" },
      { label: "Booked seats", value: "USD 720K", trend: "On target", icon: "chart" },
      { label: "Mentors", value: "231", trend: "1 exception", icon: "check" },
      { label: "Waitlisted", value: "UGX 184M", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Calendar item" },
      { key: "date", label: "Date" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Workspace" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "CLASS-OFFICE-HOURS-01",
        name: "Kijani mentor huddle",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Active",
        progress: 72,
        people: 36,
        updated: "Today 09:22",
        role: "Owner",
        value: "4",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "72%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Class Support",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "CLASS-OFFICE-HOURS-02",
        name: "Operations automation clinic",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 83,
        people: 53,
        updated: "Today 08:40",
        role: "Manager",
        value: "92%",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "83%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "CLASS-OFFICE-HOURS-03",
        name: "Governance office hours",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Attention",
        progress: 94,
        people: 70,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "14",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "94%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "CLASS-OFFICE-HOURS-04",
        name: "Executive sponsor clinic",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Approved",
        progress: 62,
        people: 87,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "USD 720K",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "62%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: ["Event title", "Date", "Time", "Time zone", "Owner", "Connected workspace"],
    chart: [64, 71, 78, 85, 92, 51, 58, 65],
    external: { domain: "class.chatoner.com", mode: "one-time signed organization handoff" },
    signature: "organization::class::office-hours::office-hour-and-organization-clinic-scheduler::v2",
  },
  "rooms-portfolio": {
    id: "rooms-portfolio",
    area: "rooms",
    areaLabel: "Build Rooms",
    title: "Build Room Portfolio",
    description:
      "Monitor instructor-owned Build Rooms by program, cohort, project, progress, approved evidence and human checkpoint.",
    group: "Build Rooms",
    icon: "layout",
    layout: "portfolio",
    model: "Organization Build Room Portfolio",
    primaryAction: "Request Build Room",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Build Rooms", value: "UGX 184M", trend: "3 actions due", icon: "layout" },
      { label: "Learners", value: "4", trend: "Evidence current", icon: "chart" },
      { label: "Human checkpoints", value: "USD 720K", trend: "Within approved threshold", icon: "check" },
      { label: "Evidence ready", value: "231", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Portfolio item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Program / Team" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "ROOMS-PORTFOLIO-01",
        name: "Kijani Market Checkout",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Healthy",
        progress: 69,
        people: 33,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "69%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Build Rooms",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "ROOMS-PORTFOLIO-02",
        name: "Support Workflow Pilot",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 80,
        people: 50,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "80%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "ROOMS-PORTFOLIO-03",
        name: "Procurement Evidence Assistant",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "Review due",
        progress: 91,
        people: 67,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "91%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "ROOMS-PORTFOLIO-04",
        name: "Responsible AI Adoption Room",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Active",
        progress: 59,
        people: 84,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "59%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [61, 68, 75, 82, 89, 48, 55, 62],
    external: null,
    signature: "organization::rooms::portfolio::organization-build-room-portfolio::v2",
  },
  "rooms-room": {
    id: "rooms-room",
    area: "rooms",
    areaLabel: "Build Rooms",
    title: "Kijani Build Room Command",
    description:
      "Inspect requirements, aggregate progress, approved artifacts, decisions and evidence without reading protected learner conversations.",
    group: "Build Rooms",
    icon: "message",
    layout: "dashboard",
    model: "Organization-Safe Build Room Command View",
    primaryAction: "Request instructor review",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Requirements", value: "268", trend: "3 actions due", icon: "message" },
      { label: "Team progress", value: "83%", trend: "Evidence current", icon: "chart" },
      { label: "Approved artifacts", value: "5", trend: "Within approved threshold", icon: "check" },
      { label: "Open conditions", value: "92%", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Priority / Domain" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "status", label: "Status" },
      { key: "updated", label: "Updated" },
    ],
    rows: [
      {
        id: "ROOMS-ROOM-01",
        name: "Acceptance criteria and scope",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Active",
        progress: 64,
        people: 28,
        updated: "Today 09:22",
        role: "Owner",
        value: "268",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "64%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Build Rooms",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "ROOMS-ROOM-02",
        name: "Current learner build progress",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 75,
        people: 45,
        updated: "Today 08:40",
        role: "Manager",
        value: "76%",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "75%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "ROOMS-ROOM-03",
        name: "Human checkpoint condition",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Attention",
        progress: 86,
        people: 62,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "231",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "86%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "ROOMS-ROOM-04",
        name: "Approved artifacts and evidence",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Approved",
        progress: 54,
        people: 79,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "83%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "54%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [56, 63, 70, 77, 84, 91, 50, 57],
    external: null,
    signature: "organization::rooms::room::organization-safe-build-room-command-view::v2",
  },
  "rooms-groups": {
    id: "rooms-groups",
    area: "rooms",
    areaLabel: "Build Rooms",
    title: "Smaller-Group Oversight",
    description:
      "View group structure, leads, intended Labs, huddle codes, branches, progress and privacy-safe aggregate evidence while preserving instructor authority.",
    group: "Smaller Groups",
    icon: "network",
    layout: "hierarchy",
    model: "Instructor-Owned Smaller-Group Oversight Map",
    primaryAction: "Request group change",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Smaller groups", value: "231", trend: "Human review required", icon: "network" },
      { label: "Learners", value: "UGX 184M", trend: "Updated today", icon: "chart" },
      { label: "Private channels", value: "4", trend: "On target", icon: "check" },
      { label: "Active huddles", value: "USD 720K", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Team / Entity" },
      { key: "owner", label: "Lead" },
      { key: "people", label: "People" },
      { key: "progress", label: "Readiness" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "ROOMS-GROUPS-01",
        name: "Checkout Experience Group",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 66,
        people: 30,
        updated: "Today 09:22",
        role: "Owner",
        value: "231",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "66%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Smaller Groups",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "ROOMS-GROUPS-02",
        name: "Payment Fixture Group",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 77,
        people: 47,
        updated: "Today 08:40",
        role: "Manager",
        value: "83%",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "77%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "ROOMS-GROUPS-03",
        name: "Accessibility Evaluation Group",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 88,
        people: 64,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "12",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "88%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "ROOMS-GROUPS-04",
        name: "Research and Data Group",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 56,
        people: 81,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "UGX 184M",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "56%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "ROOMS-GROUPS-05",
        name: "Evidence and Reflection Group",
        owner: "Ronald Isabirye",
        scope: "Finance & Procurement",
        status: "On track",
        progress: 67,
        people: 98,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "5",
        target: "85%",
        level: "Optimized",
        trend: "+8% this cycle",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "67%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
      {
        id: "ROOMS-GROUPS-06",
        name: "Pilot Readiness Group",
        owner: "Amina Mutesi",
        scope: "People & Culture",
        status: "Review due",
        progress: 78,
        people: 115,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "78%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Smaller Groups",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [58, 65, 72, 79, 86, 93, 52, 59],
    external: null,
    signature: "organization::rooms::groups::instructor-owned-smaller-group-oversight-map::v2",
  },
  "rooms-evidence": {
    id: "rooms-evidence",
    area: "rooms",
    areaLabel: "Build Rooms",
    title: "Build Room Evidence Review",
    description:
      "Review organization-visible Skill, System and Proof evidence, requirement links, human decisions and unresolved conditions.",
    group: "Evidence",
    icon: "award",
    layout: "evidence",
    model: "Build Room Approved Evidence and Checkpoint Review",
    primaryAction: "Request evidence review",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Evidence items", value: "12", trend: "Evidence current", icon: "award" },
      { label: "Accepted", value: "68%", trend: "Within approved threshold", icon: "chart" },
      { label: "Pending review", value: "14", trend: "Human review required", icon: "check" },
      { label: "Conditions", value: "76%", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Evidence item" },
      { key: "source", label: "Source" },
      { key: "owner", label: "Reviewer" },
      { key: "evidence", label: "Coverage" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "ROOMS-EVIDENCE-01",
        name: "Annotated accessibility defect",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Approved",
        progress: 68,
        people: 32,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "68%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Evidence",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "ROOMS-EVIDENCE-02",
        name: "PR #184 learner review",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Ready",
        progress: 79,
        people: 49,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "79%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "ROOMS-EVIDENCE-03",
        name: "18-check evaluation run",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "Review due",
        progress: 90,
        people: 66,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "90%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "ROOMS-EVIDENCE-04",
        name: "Staging widget verification",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Pending",
        progress: 58,
        people: 83,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "58%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "ROOMS-EVIDENCE-05",
        name: "Instructor conditional approval",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Approved",
        progress: 69,
        people: 100,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "69%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [60, 67, 74, 81, 88, 47, 54, 61],
    external: null,
    signature: "organization::rooms::evidence::build-room-approved-evidence-and-checkpoint-review::v2",
  },
  "rooms-permissions": {
    id: "rooms-permissions",
    area: "rooms",
    areaLabel: "Build Rooms",
    title: "Room Permission Boundaries",
    description:
      "Define organization, instructor, learner, reviewer and AI-agent visibility, actions, data scopes, purpose, expiry and emergency revocation.",
    group: "Room Governance",
    icon: "lock",
    layout: "matrix",
    model: "Build Room Role, Data and AI Scope Matrix",
    primaryAction: "Review room access",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Role types", value: "68%", trend: "3 actions due", icon: "lock" },
      { label: "Scoped grants", value: "14", trend: "Evidence current", icon: "chart" },
      { label: "Expiring", value: "76%", trend: "Within approved threshold", icon: "check" },
      { label: "Policy exceptions", value: "12", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Subject" },
      { key: "scope", label: "Scope" },
      { key: "level", label: "Level" },
      { key: "progress", label: "Coverage" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "ROOMS-PERMISSIONS-01",
        name: "Organization aggregate viewer",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Room Governance",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "ROOMS-PERMISSIONS-02",
        name: "Instructor and assessor",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "ROOMS-PERMISSIONS-03",
        name: "Learner and group member",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "ROOMS-PERMISSIONS-04",
        name: "Authorized external reviewer",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "ROOMS-PERMISSIONS-05",
        name: "Permission-scoped AI Builder",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature: "organization::rooms::permissions::build-room-role,-data-and-ai-scope-matrix::v2",
  },
  "labs-connection": {
    id: "labs-connection",
    area: "labs",
    areaLabel: "Connected AI Labs",
    title: "Chatoner AI Labs Connection",
    description:
      "Coordinate secure one-time handoffs to labs.chatoner.com while Labs independently owns practical runtime, tools, compute, safety and Lab-native evidence.",
    group: "Connected Labs",
    icon: "link",
    layout: "connection",
    model: "Independent Chatoner AI Labs Handoff Gateway",
    primaryAction: "Open Labs workspace",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Labs services", value: "UGX 184M", trend: "Human review required", icon: "link" },
      { label: "Entitlements synced", value: "4", trend: "Updated today", icon: "chart" },
      { label: "Handoff health", value: "USD 720K", trend: "On target", icon: "check" },
      { label: "Evidence callbacks", value: "231", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Connected service" },
      { key: "provider", label: "Provider" },
      { key: "scope", label: "Data / authority scope" },
      { key: "updated", label: "Last check" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LABS-CONNECTION-01",
        name: "Organization Labs workspace",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Healthy",
        progress: 69,
        people: 33,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "69%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Connected Labs",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LABS-CONNECTION-02",
        name: "Entitlement handoff",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 80,
        people: 50,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "80%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LABS-CONNECTION-03",
        name: "Assignment-intended Lab handoff",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "Review due",
        progress: 91,
        people: 67,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "91%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LABS-CONNECTION-04",
        name: "Returned evidence callback",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Active",
        progress: 59,
        people: 84,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "59%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Connected service",
      "Client / tenant reference",
      "Authorized scopes",
      "Return URL",
      "Data owner",
      "Security review note",
    ],
    chart: [61, 68, 75, 82, 89, 48, 55, 62],
    external: { domain: "labs.chatoner.com", mode: "entitlement-bound organization handoff" },
    signature: "organization::labs::connection::independent-chatoner-ai-labs-handoff-gateway::v2",
  },
  "labs-catalogue": {
    id: "labs-catalogue",
    area: "labs",
    areaLabel: "Connected AI Labs",
    title: "Eligible & Intended Labs",
    description:
      "Review Labs approved for organizational programs, roles, assignments and outcomes with exact environment, prerequisites and visibility boundaries.",
    group: "Lab Catalogue",
    icon: "flask",
    layout: "library",
    model: "Organization Eligible and Intended Lab Catalogue",
    primaryAction: "Add intended Lab",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Eligible Labs", value: "12", trend: "+8% this cycle", icon: "flask" },
      { label: "Programs mapped", value: "68%", trend: "3 actions due", icon: "chart" },
      { label: "Included", value: "14", trend: "Evidence current", icon: "check" },
      { label: "Advanced", value: "76%", trend: "Within approved threshold", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Item" },
      { key: "category", label: "Category" },
      { key: "language", label: "Language" },
      { key: "updated", label: "Updated / Expiry" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LABS-CATALOGUE-01",
        name: "AI-App Builder Lab",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Pending",
        progress: 68,
        people: 32,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "68%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Lab Catalogue",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LABS-CATALOGUE-02",
        name: "Agentic Workflow Lab",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 79,
        people: 49,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "79%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LABS-CATALOGUE-03",
        name: "Conversational AI Lab",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "On track",
        progress: 90,
        people: 66,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "90%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LABS-CATALOGUE-04",
        name: "Data & Predictive AI Lab",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 58,
        people: 83,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "58%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "LABS-CATALOGUE-05",
        name: "AI Governance Simulation Lab",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Active",
        progress: 69,
        people: 100,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "69%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [60, 67, 74, 81, 88, 47, 54, 61],
    external: { domain: "labs.chatoner.com", mode: "entitlement-bound organization handoff" },
    signature: "organization::labs::catalogue::organization-eligible-and-intended-lab-catalogue::v2",
  },
  "labs-entitlements": {
    id: "labs-entitlements",
    area: "labs",
    areaLabel: "Connected AI Labs",
    title: "Lab Entitlements & Capacity",
    description:
      "Manage seats, time, token, compute and environment entitlements by program, cohort and learner while enforcing sponsorship conditions.",
    group: "Lab Administration",
    icon: "key",
    layout: "capacity",
    model: "Organization Lab Entitlement and Capacity Register",
    primaryAction: "Allocate entitlement",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Seats", value: "68%", trend: "Evidence current", icon: "key" },
      { label: "Assigned", value: "14", trend: "Within approved threshold", icon: "chart" },
      { label: "Compute hours", value: "76%", trend: "Human review required", icon: "check" },
      { label: "Exceptions", value: "12", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Resource" },
      { key: "capacity", label: "Capacity" },
      { key: "usage", label: "Assigned / Used" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LABS-ENTITLEMENTS-01",
        name: "AI-App Builder cohort entitlement",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Lab Administration",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LABS-ENTITLEMENTS-02",
        name: "Workflow Lab team entitlement",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LABS-ENTITLEMENTS-03",
        name: "Governance simulation entitlement",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LABS-ENTITLEMENTS-04",
        name: "Advanced compute exception",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: { domain: "labs.chatoner.com", mode: "entitlement-bound organization handoff" },
    signature: "organization::labs::entitlements::organization-lab-entitlement-and-capacity-register::v2",
  },
  "labs-runs": {
    id: "labs-runs",
    area: "labs",
    areaLabel: "Connected AI Labs",
    title: "Synced Lab Runs",
    description:
      "Review authorized run status, environment, learner eligibility, duration, policy state and return readiness without entering learner experiments.",
    group: "Lab Operations",
    icon: "pulse",
    layout: "sessions",
    model: "Read-Only Synced Lab Run Operations Register",
    primaryAction: "Reconcile run status",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Runs this month", value: "USD 720K", trend: "Human review required", icon: "pulse" },
      { label: "Completed", value: "231", trend: "Updated today", icon: "chart" },
      { label: "In progress", value: "UGX 184M", trend: "On target", icon: "check" },
      { label: "Needs support", value: "4", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Session / Run" },
      { key: "code", label: "Code / Reference" },
      { key: "owner", label: "Host / Owner" },
      { key: "date", label: "Schedule" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LABS-RUNS-01",
        name: "Kijani checkout run 184",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 63,
        people: 27,
        updated: "Today 09:22",
        role: "Owner",
        value: "USD 720K",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "63%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Lab Operations",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LABS-RUNS-02",
        name: "Support triage workflow run 96",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 74,
        people: 44,
        updated: "Today 08:40",
        role: "Manager",
        value: "268",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "74%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LABS-RUNS-03",
        name: "Procurement evidence run 44",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 85,
        people: 61,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "76%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "85%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LABS-RUNS-04",
        name: "Governance scenario run 27",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 96,
        people: 78,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "231",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "96%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [55, 62, 69, 76, 83, 90, 49, 56],
    external: { domain: "labs.chatoner.com", mode: "entitlement-bound organization handoff" },
    signature: "organization::labs::runs::read-only-synced-lab-run-operations-register::v2",
  },
  "labs-evidence": {
    id: "labs-evidence",
    area: "labs",
    areaLabel: "Connected AI Labs",
    title: "Returned Lab Evidence",
    description:
      "Reconcile signed evidence returned by Labs to assignment, learner, project and credential requirements with consent and human review.",
    group: "Lab Evidence",
    icon: "award",
    layout: "evidence",
    model: "Returned Lab Evidence Reconciliation Workbench",
    primaryAction: "Reconcile evidence",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Returned items", value: "83%", trend: "On target", icon: "award" },
      { label: "Reconciled", value: "5", trend: "1 exception", icon: "chart" },
      { label: "Consent checks", value: "92%", trend: "+8% this cycle", icon: "check" },
      { label: "Human review", value: "268", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Evidence item" },
      { key: "source", label: "Source" },
      { key: "owner", label: "Reviewer" },
      { key: "evidence", label: "Coverage" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LABS-EVIDENCE-01",
        name: "Checkout preview and tests",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 67,
        people: 31,
        updated: "Today 09:22",
        role: "Owner",
        value: "83%",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "67%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Lab Evidence",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LABS-EVIDENCE-02",
        name: "Workflow decision trace",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Ready",
        progress: 78,
        people: 48,
        updated: "Today 08:40",
        role: "Manager",
        value: "12",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "78%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LABS-EVIDENCE-03",
        name: "Procurement evidence map",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 89,
        people: 65,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "UGX 184M",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "89%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LABS-EVIDENCE-04",
        name: "Governance scenario reflection",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "Pending",
        progress: 57,
        people: 82,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "5",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "57%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [59, 66, 73, 80, 87, 46, 53, 60],
    external: { domain: "labs.chatoner.com", mode: "entitlement-bound organization handoff" },
    signature: "organization::labs::evidence::returned-lab-evidence-reconciliation-workbench::v2",
  },
  "labs-analytics": {
    id: "labs-analytics",
    area: "labs",
    areaLabel: "Connected AI Labs",
    title: "Lab Adoption & Usage Analytics",
    description:
      "Analyze privacy-thresholded Lab adoption, session completion, entitlement utilization, cost, support needs and outcome relationships.",
    group: "Lab Analytics",
    icon: "chart",
    layout: "analytics",
    model: "Lab Adoption, Cost and Outcome Analytics Studio",
    primaryAction: "Generate Lab analysis",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Completion rate", value: "12", trend: "1 exception", icon: "chart" },
      { label: "Utilization", value: "68%", trend: "+8% this cycle", icon: "chart" },
      { label: "Evidence return", value: "14", trend: "3 actions due", icon: "check" },
      { label: "Cost per run", value: "76%", trend: "Evidence current", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Measure" },
      { key: "value", label: "Value" },
      { key: "trend", label: "Trend" },
      { key: "source", label: "Evidence source" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "LABS-ANALYTICS-01",
        name: "Lab session completion",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Pending",
        progress: 68,
        people: 32,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "68%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Lab Analytics",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "LABS-ANALYTICS-02",
        name: "Entitlement utilization",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 79,
        people: 49,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "79%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "LABS-ANALYTICS-03",
        name: "Returned evidence rate",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "On track",
        progress: 90,
        people: 66,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "90%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "LABS-ANALYTICS-04",
        name: "Support intervention rate",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 58,
        people: 83,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "58%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "LABS-ANALYTICS-05",
        name: "Cost per completed run",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Active",
        progress: 69,
        people: 100,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "69%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [60, 67, 74, 81, 88, 47, 54, 61],
    external: { domain: "labs.chatoner.com", mode: "entitlement-bound organization handoff" },
    signature: "organization::labs::analytics::lab-adoption,-cost-and-outcome-analytics-studio::v2",
  },
  "projects-portfolio": {
    id: "projects-portfolio",
    area: "projects",
    areaLabel: "Projects & Credentials",
    title: "Project Portfolio",
    description:
      "Manage workflow projects, capstones and controlled pilots by owner, stage, risk, modeled value, evidence and human decision.",
    group: "Projects",
    icon: "folder",
    layout: "portfolio",
    model: "Organization AI Project and Capstone Portfolio",
    primaryAction: "Create project",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Active projects", value: "4", trend: "On target", icon: "folder" },
      { label: "Controlled pilots", value: "USD 720K", trend: "1 exception", icon: "chart" },
      { label: "Modeled value", value: "231", trend: "+8% this cycle", icon: "check" },
      { label: "Human gates", value: "UGX 184M", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Portfolio item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Program / Team" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROJECTS-PORTFOLIO-01",
        name: "Kijani Checkout Improvement",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Active",
        progress: 72,
        people: 36,
        updated: "Today 09:22",
        role: "Owner",
        value: "4",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "72%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Projects",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROJECTS-PORTFOLIO-02",
        name: "Customer Support Triage",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 83,
        people: 53,
        updated: "Today 08:40",
        role: "Manager",
        value: "92%",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "83%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROJECTS-PORTFOLIO-03",
        name: "Procurement Evidence Review",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Attention",
        progress: 94,
        people: 70,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "14",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "94%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROJECTS-PORTFOLIO-04",
        name: "AI Adoption Playbook",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Approved",
        progress: 62,
        people: 87,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "USD 720K",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "62%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [64, 71, 78, 85, 92, 51, 58, 65],
    external: null,
    signature: "organization::projects::portfolio::organization-ai-project-and-capstone-portfolio::v2",
  },
  "projects-workflow-projects": {
    id: "projects-workflow-projects",
    area: "projects",
    areaLabel: "Projects & Credentials",
    title: "Workflow Project Delivery",
    description:
      "Move organization AI workflow projects through discover, design, build, pilot, approve, deploy, adopt and measure with traceability.",
    group: "Projects",
    icon: "workflow",
    layout: "board",
    model: "Controlled Workflow Project Delivery Board",
    primaryAction: "Add project work",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Workflow projects", value: "12", trend: "Evidence current", icon: "workflow" },
      { label: "In build", value: "68%", trend: "Within approved threshold", icon: "chart" },
      { label: "In pilot", value: "14", trend: "Human review required", icon: "check" },
      { label: "Measured", value: "76%", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Requirement" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROJECTS-WORKFLOW-PROJECTS-01",
        name: "Discover user and process need",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Active",
        progress: 80,
        people: 44,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "80%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Projects",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROJECTS-WORKFLOW-PROJECTS-02",
        name: "Design governed workflow",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 91,
        people: 61,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "91%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROJECTS-WORKFLOW-PROJECTS-03",
        name: "Build controlled prototype",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Attention",
        progress: 59,
        people: 78,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "59%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROJECTS-WORKFLOW-PROJECTS-04",
        name: "Pilot with approved users",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Approved",
        progress: 70,
        people: 95,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "70%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PROJECTS-WORKFLOW-PROJECTS-05",
        name: "Approve and deploy",
        owner: "Sarah Atim",
        scope: "Operations team",
        status: "Pending",
        progress: 81,
        people: 112,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "On target",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "81%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
      {
        id: "PROJECTS-WORKFLOW-PROJECTS-06",
        name: "Adopt and measure",
        owner: "Joseph Okello",
        scope: "Finance & Procurement",
        status: "Healthy",
        progress: 92,
        people: 20,
        updated: "Today 09:22",
        role: "Owner",
        value: "92%",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "92%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Projects",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [72, 79, 86, 93, 52, 59, 66, 73],
    external: null,
    signature: "organization::projects::workflow-projects::controlled-workflow-project-delivery-board::v2",
  },
  "projects-capstones": {
    id: "projects-capstones",
    area: "projects",
    areaLabel: "Projects & Credentials",
    title: "Capstones & Review Panels",
    description:
      "Coordinate capstone readiness, presentation panels, evaluator scope, evidence, moderation, outcome and appeal boundaries.",
    group: "Capstones",
    icon: "trophy",
    layout: "evidence",
    model: "Capstone Submission and Human Review Panel",
    primaryAction: "Schedule review panel",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Capstones", value: "4", trend: "Evidence current", icon: "trophy" },
      { label: "Panels scheduled", value: "USD 720K", trend: "Within approved threshold", icon: "chart" },
      { label: "Evidence complete", value: "231", trend: "Human review required", icon: "check" },
      { label: "Decisions due", value: "UGX 184M", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Evidence item" },
      { key: "source", label: "Source" },
      { key: "owner", label: "Reviewer" },
      { key: "evidence", label: "Coverage" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROJECTS-CAPSTONES-01",
        name: "Kijani accessible checkout",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Approved",
        progress: 72,
        people: 36,
        updated: "Today 09:22",
        role: "Owner",
        value: "4",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "72%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Capstones",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROJECTS-CAPSTONES-02",
        name: "Governed support workflow",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 83,
        people: 53,
        updated: "Today 08:40",
        role: "Manager",
        value: "92%",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "83%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROJECTS-CAPSTONES-03",
        name: "Procurement evidence assistant",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Review due",
        progress: 94,
        people: 70,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "14",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "94%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROJECTS-CAPSTONES-04",
        name: "Responsible adoption program",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Pending",
        progress: 62,
        people: 87,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "USD 720K",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "62%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [64, 71, 78, 85, 92, 51, 58, 65],
    external: null,
    signature: "organization::projects::capstones::capstone-submission-and-human-review-panel::v2",
  },
  "projects-credentials": {
    id: "projects-credentials",
    area: "projects",
    areaLabel: "Projects & Credentials",
    title: "Credential Readiness",
    description:
      "Track recommendation, evidence completeness, assessment state, human approval, issuance, verification and revocation without issuing credentials directly.",
    group: "Credentials",
    icon: "certificate",
    layout: "verification",
    model: "Organization Credential Readiness and Verification Pipeline",
    primaryAction: "Request readiness review",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Learners tracked", value: "14", trend: "Within approved threshold", icon: "certificate" },
      { label: "Evidence-ready", value: "76%", trend: "Human review required", icon: "chart" },
      { label: "Assessment-ready", value: "12", trend: "Updated today", icon: "check" },
      { label: "Issued", value: "68%", trend: "On target", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Credential / Record" },
      { key: "code", label: "Verification ID" },
      { key: "owner", label: "Learner / Owner" },
      { key: "progress", label: "Readiness" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROJECTS-CREDENTIALS-01",
        name: "AI Product Builder",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 74,
        people: 38,
        updated: "Today 09:22",
        role: "Owner",
        value: "14",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "74%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Credentials",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROJECTS-CREDENTIALS-02",
        name: "AI Workflow Practitioner",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 85,
        people: 55,
        updated: "Today 08:40",
        role: "Manager",
        value: "USD 720K",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "85%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROJECTS-CREDENTIALS-03",
        name: "AI Governance Practitioner",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 96,
        people: 72,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "268",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "96%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROJECTS-CREDENTIALS-04",
        name: "Organization AI Champion",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 64,
        people: 89,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "76%",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "64%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [66, 73, 80, 87, 46, 53, 60, 67],
    external: null,
    signature:
      "organization::projects::credentials::organization-credential-readiness-and-verification-pipeline::v2",
  },
  "projects-evidence": {
    id: "projects-evidence",
    area: "projects",
    areaLabel: "Projects & Credentials",
    title: "Skill, System & Proof Centre",
    description:
      "Connect capability claims to learner contribution, system artifacts, tests, AI assistance, human decisions and observable outcomes.",
    group: "Evidence",
    icon: "award",
    layout: "map",
    model: "Cross-Project Skill System and Proof Evidence Map",
    primaryAction: "Add evidence source",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Claims", value: "68%", trend: "3 actions due", icon: "award" },
      { label: "Linked systems", value: "14", trend: "Evidence current", icon: "chart" },
      { label: "Proof items", value: "76%", trend: "Within approved threshold", icon: "check" },
      { label: "Gaps", value: "12", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Outcome / Node" },
      { key: "scope", label: "Connected to" },
      { key: "owner", label: "Owner" },
      { key: "evidence", label: "Evidence" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROJECTS-EVIDENCE-01",
        name: "Problem and outcome claim",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Evidence",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROJECTS-EVIDENCE-02",
        name: "Learner decision contribution",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROJECTS-EVIDENCE-03",
        name: "System artifact and version",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROJECTS-EVIDENCE-04",
        name: "Evaluation and human check",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PROJECTS-EVIDENCE-05",
        name: "Observable pilot outcome",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature: "organization::projects::evidence::cross-project-skill-system-and-proof-evidence-map::v2",
  },
  "governance-overview": {
    id: "governance-overview",
    area: "governance",
    areaLabel: "Governance & Risk",
    title: "Governance Command",
    description:
      "Monitor policy maturity, approved tools, risk, acknowledgements, incidents, audit, human authority and cross-platform control coverage.",
    group: "Governance",
    icon: "shield",
    layout: "dashboard",
    model: "Organization AI Governance Command Centre",
    primaryAction: "Create governance action",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Policy coverage", value: "92%", trend: "3 actions due", icon: "shield" },
      { label: "Approved tools", value: "268", trend: "Evidence current", icon: "chart" },
      { label: "Open risks", value: "83%", trend: "Within approved threshold", icon: "check" },
      { label: "Acknowledgement", value: "5", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Priority / Domain" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "status", label: "Status" },
      { key: "updated", label: "Updated" },
    ],
    rows: [
      {
        id: "GOVERNANCE-OVERVIEW-01",
        name: "Policy lifecycle coverage",
        owner: "Mariam Nansubuga",
        scope: "Customer Experience",
        status: "Ready",
        progress: 73,
        people: 37,
        updated: "Today 09:22",
        role: "Owner",
        value: "92%",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "73%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Governance",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "GOVERNANCE-OVERVIEW-02",
        name: "Approved tool coverage",
        owner: "David Kato",
        scope: "Technology & Data",
        status: "Attention",
        progress: 84,
        people: 54,
        updated: "Today 08:40",
        role: "Manager",
        value: "14",
        target: "100%",
        level: "Established",
        trend: "Human review required",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "84%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "GOVERNANCE-OVERVIEW-03",
        name: "Risk treatment progress",
        owner: "Peter Ouma",
        scope: "Executive committee",
        status: "Approved",
        progress: 95,
        people: 71,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "USD 720K",
        target: "UGX 200M",
        level: "Developing",
        trend: "Updated today",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "95%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "GOVERNANCE-OVERVIEW-04",
        name: "Acknowledgement completion",
        owner: "Sarah Atim",
        scope: "October 2026 cohort",
        status: "Pending",
        progress: 63,
        people: 88,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "268",
        target: "0 exceptions",
        level: "Initial",
        trend: "On target",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "63%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "GOVERNANCE-OVERVIEW-05",
        name: "Privileged access review",
        owner: "Joseph Okello",
        scope: "Customer Experience",
        status: "Healthy",
        progress: 74,
        people: 105,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "76%",
        target: "85%",
        level: "Optimized",
        trend: "1 exception",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "74%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [65, 72, 79, 86, 93, 52, 59, 66],
    external: null,
    signature: "organization::governance::overview::organization-ai-governance-command-centre::v2",
  },
  "governance-policies": {
    id: "governance-policies",
    area: "governance",
    areaLabel: "Governance & Risk",
    title: "Policy Lifecycle",
    description:
      "Manage policy drafting, review, approval, publication, acknowledgement, exception, review date and retirement with version history.",
    group: "Policies",
    icon: "file",
    layout: "policy",
    model: "Organization AI Policy Lifecycle and Acknowledgement Console",
    primaryAction: "Draft policy",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Published", value: "92%", trend: "Human review required", icon: "file" },
      { label: "Draft", value: "268", trend: "Updated today", icon: "chart" },
      { label: "Review due", value: "83%", trend: "On target", icon: "check" },
      { label: "Exceptions", value: "5", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Policy / Control" },
      { key: "version", label: "Version" },
      { key: "owner", label: "Owner" },
      { key: "deadline", label: "Review date" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "GOVERNANCE-POLICIES-01",
        name: "Responsible AI Use Policy",
        owner: "Mariam Nansubuga",
        scope: "Customer Experience",
        status: "Approved",
        progress: 73,
        people: 37,
        updated: "Today 09:22",
        role: "Owner",
        value: "92%",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "73%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Policies",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "GOVERNANCE-POLICIES-02",
        name: "Approved AI Tools Policy",
        owner: "David Kato",
        scope: "Technology & Data",
        status: "Active",
        progress: 84,
        people: 54,
        updated: "Today 08:40",
        role: "Manager",
        value: "14",
        target: "100%",
        level: "Established",
        trend: "Human review required",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "84%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "GOVERNANCE-POLICIES-03",
        name: "Learner and Employee Data Policy",
        owner: "Peter Ouma",
        scope: "Executive committee",
        status: "Review due",
        progress: 95,
        people: 71,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "USD 720K",
        target: "UGX 200M",
        level: "Developing",
        trend: "Updated today",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "95%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "GOVERNANCE-POLICIES-04",
        name: "Human Decision and Override Policy",
        owner: "Sarah Atim",
        scope: "October 2026 cohort",
        status: "Pending",
        progress: 63,
        people: 88,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "268",
        target: "0 exceptions",
        level: "Initial",
        trend: "On target",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "63%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "GOVERNANCE-POLICIES-05",
        name: "AI Incident Response Policy",
        owner: "Joseph Okello",
        scope: "Customer Experience",
        status: "Approved",
        progress: 74,
        people: 105,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "76%",
        target: "85%",
        level: "Optimized",
        trend: "1 exception",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "74%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Policy or control name",
      "Version",
      "Owner",
      "Effective date",
      "Review date",
      "Approval reason",
    ],
    chart: [65, 72, 79, 86, 93, 52, 59, 66],
    external: null,
    signature:
      "organization::governance::policies::organization-ai-policy-lifecycle-and-acknowledgement-console::v2",
    accessRoles: ["owner", "governance-lead", "procurement-officer", "auditor"],
    manageRoles: ["owner", "governance-lead"],
    approveRoles: ["owner", "governance-lead"],
  },
  "governance-approved-tools": {
    id: "governance-approved-tools",
    area: "governance",
    areaLabel: "Governance & Risk",
    title: "Approved AI Tool Catalogue",
    description:
      "Review approved tools, models, vendors, intended purposes, data classes, conditions, owners, expiry and prohibited uses.",
    group: "Approved Tools",
    icon: "bot",
    layout: "library",
    model: "Approved AI Tool, Model and Data-Use Catalogue",
    primaryAction: "Submit tool for review",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Approved tools", value: "83%", trend: "On target", icon: "bot" },
      { label: "Conditional", value: "5", trend: "1 exception", icon: "chart" },
      { label: "Reviews due", value: "92%", trend: "+8% this cycle", icon: "check" },
      { label: "Blocked", value: "268", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Item" },
      { key: "category", label: "Category" },
      { key: "language", label: "Language" },
      { key: "updated", label: "Updated / Expiry" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "GOVERNANCE-APPROVED-TOOLS-01",
        name: "Chatoner AI Labs",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 79,
        people: 43,
        updated: "Today 09:22",
        role: "Owner",
        value: "83%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "79%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Approved Tools",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "GOVERNANCE-APPROVED-TOOLS-02",
        name: "Microsoft Copilot",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 90,
        people: 60,
        updated: "Today 08:40",
        role: "Manager",
        value: "12",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "90%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "GOVERNANCE-APPROVED-TOOLS-03",
        name: "OpenAI API via approved gateway",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 58,
        people: 77,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "UGX 184M",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "58%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "GOVERNANCE-APPROVED-TOOLS-04",
        name: "Internal retrieval assistant",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 69,
        people: 94,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "5",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "69%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "GOVERNANCE-APPROVED-TOOLS-05",
        name: "Unreviewed browser extension",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 80,
        people: 111,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "68%",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "80%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [71, 78, 85, 92, 51, 58, 65, 72],
    external: null,
    signature: "organization::governance::approved-tools::approved-ai-tool,-model-and-data-use-catalogue::v2",
    manageRoles: ["owner", "governance-lead"],
    approveRoles: ["owner", "governance-lead"],
  },
  "governance-risk-register": {
    id: "governance-risk-register",
    area: "governance",
    areaLabel: "Governance & Risk",
    title: "AI Risk Register",
    description:
      "Capture risk statements, affected processes, likelihood, impact, controls, treatment owners, due dates and accepted residual risk.",
    group: "Risk",
    icon: "alert",
    layout: "queue",
    model: "Organization AI Risk Triage and Treatment Register",
    primaryAction: "Register risk",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Open risks", value: "231", trend: "Evidence current", icon: "alert" },
      { label: "High risks", value: "UGX 184M", trend: "Within approved threshold", icon: "chart" },
      { label: "Treatments due", value: "4", trend: "Human review required", icon: "check" },
      { label: "Accepted residual", value: "USD 720K", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "deadline", label: "Due" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "GOVERNANCE-RISK-REGISTER-01",
        name: "Sensitive data entered into unapproved AI",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "Urgent",
        progress: 78,
        people: 42,
        updated: "Today 09:22",
        role: "Owner",
        value: "231",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "78%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Risk",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "GOVERNANCE-RISK-REGISTER-02",
        name: "Automation acts without required approval",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 89,
        people: 59,
        updated: "Today 08:40",
        role: "Manager",
        value: "83%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "89%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "GOVERNANCE-RISK-REGISTER-03",
        name: "Model response influences regulated decision",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "On track",
        progress: 57,
        people: 76,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "12",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "57%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "GOVERNANCE-RISK-REGISTER-04",
        name: "Learner evidence exposed across teams",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 68,
        people: 93,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "UGX 184M",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "68%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "GOVERNANCE-RISK-REGISTER-05",
        name: "Vendor retention exceeds policy",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Urgent",
        progress: 79,
        people: 110,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "5",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "79%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [70, 77, 84, 91, 50, 57, 64, 71],
    external: null,
    signature:
      "organization::governance::risk-register::organization-ai-risk-triage-and-treatment-register::v2",
    accessRoles: ["owner", "governance-lead", "procurement-officer", "auditor"],
    manageRoles: ["owner", "governance-lead"],
    approveRoles: ["owner", "governance-lead"],
  },
  "governance-acknowledgements": {
    id: "governance-acknowledgements",
    area: "governance",
    areaLabel: "Governance & Risk",
    title: "Policy Acknowledgements",
    description:
      "Track assigned acknowledgements, reminders, completion, exceptions, role changes and evidence by policy version and audience.",
    group: "Policy Adoption",
    icon: "check",
    layout: "onboarding",
    model: "Policy Acknowledgement and Exception Campaign",
    primaryAction: "Launch campaign",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Campaigns", value: "UGX 184M", trend: "Updated today", icon: "check" },
      { label: "Assigned", value: "4", trend: "On target", icon: "chart" },
      { label: "Completed", value: "USD 720K", trend: "1 exception", icon: "check" },
      { label: "Exceptions", value: "231", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Journey / Cohort" },
      { key: "owner", label: "Owner" },
      { key: "progress", label: "Progress" },
      { key: "deadline", label: "Next date" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "GOVERNANCE-ACKNOWLEDGEMENTS-01",
        name: "Responsible AI policy 3.0",
        owner: "Mariam Nansubuga",
        scope: "Customer Experience",
        status: "Ready",
        progress: 81,
        people: 45,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "81%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Policy Adoption",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "GOVERNANCE-ACKNOWLEDGEMENTS-02",
        name: "Approved tool standard 2.2",
        owner: "David Kato",
        scope: "Technology & Data",
        status: "Attention",
        progress: 92,
        people: 62,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "Human review required",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "92%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "GOVERNANCE-ACKNOWLEDGEMENTS-03",
        name: "Data handling standard 4.1",
        owner: "Peter Ouma",
        scope: "Executive committee",
        status: "Approved",
        progress: 60,
        people: 79,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Updated today",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "60%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "GOVERNANCE-ACKNOWLEDGEMENTS-04",
        name: "Human review standard 1.6",
        owner: "Sarah Atim",
        scope: "October 2026 cohort",
        status: "Pending",
        progress: 71,
        people: 96,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "On target",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "71%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [73, 80, 87, 46, 53, 60, 67, 74],
    external: null,
    signature:
      "organization::governance::acknowledgements::policy-acknowledgement-and-exception-campaign::v2",
  },
  "governance-audit": {
    id: "governance-audit",
    area: "governance",
    areaLabel: "Governance & Risk",
    title: "Governance Audit Trail",
    description:
      "Inspect attributable policy, tool, risk, access, approval and emergency-control events with correlation and evidence references.",
    group: "Audit",
    icon: "history",
    layout: "table",
    model: "Immutable Governance Decision and Activity Ledger",
    primaryAction: "Export audit evidence",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Events", value: "5", trend: "3 actions due", icon: "history" },
      { label: "Privileged actions", value: "92%", trend: "Evidence current", icon: "chart" },
      { label: "Exports", value: "268", trend: "Within approved threshold", icon: "check" },
      { label: "Integrity checks", value: "83%", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Record / Event" },
      { key: "owner", label: "Actor / Owner" },
      { key: "scope", label: "Scope" },
      { key: "updated", label: "Time" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "GOVERNANCE-AUDIT-01",
        name: "Policy version 3.0 approved",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Audit",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "GOVERNANCE-AUDIT-02",
        name: "Finance role scope changed",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "GOVERNANCE-AUDIT-03",
        name: "Lab entitlement exception approved",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "GOVERNANCE-AUDIT-04",
        name: "Risk treatment accepted",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "GOVERNANCE-AUDIT-05",
        name: "Emergency session revoked",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Attention",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::governance::audit::immutable-governance-decision-and-activity-ledger::v2",
    accessRoles: ["owner", "governance-lead", "auditor"],
  },
  "integrations-catalogue": {
    id: "integrations-catalogue",
    area: "integrations",
    areaLabel: "Integrations",
    title: "Integration Catalogue",
    description:
      "Manage identity, HRIS, LMS, calendar, collaboration, finance and workflow connectors with owners, permissions, health and data scope.",
    group: "Integration Portfolio",
    icon: "link",
    layout: "connection",
    model: "Organization Connector Catalogue and Data-Scope Registry",
    primaryAction: "Add integration",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Connectors", value: "268", trend: "+8% this cycle", icon: "link" },
      { label: "Connected", value: "83%", trend: "3 actions due", icon: "chart" },
      { label: "Limited", value: "5", trend: "Evidence current", icon: "check" },
      { label: "Review due", value: "92%", trend: "Within approved threshold", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Connected service" },
      { key: "provider", label: "Provider" },
      { key: "scope", label: "Data / authority scope" },
      { key: "updated", label: "Last check" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "INTEGRATIONS-CATALOGUE-01",
        name: "Microsoft Entra ID",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Pending",
        progress: 76,
        people: 40,
        updated: "Today 09:22",
        role: "Owner",
        value: "268",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "76%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Integration Portfolio",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "INTEGRATIONS-CATALOGUE-02",
        name: "Workday HRIS",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 87,
        people: 57,
        updated: "Today 08:40",
        role: "Manager",
        value: "76%",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "87%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "INTEGRATIONS-CATALOGUE-03",
        name: "Moodle LMS",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "On track",
        progress: 55,
        people: 74,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "231",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "55%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "INTEGRATIONS-CATALOGUE-04",
        name: "Google Calendar",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 66,
        people: 91,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "83%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "66%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "INTEGRATIONS-CATALOGUE-05",
        name: "Microsoft Teams",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Active",
        progress: 77,
        people: 108,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "12",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "77%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
      {
        id: "INTEGRATIONS-CATALOGUE-06",
        name: "Signed Webhooks",
        owner: "Mariam Nansubuga",
        scope: "Organization-wide",
        status: "Ready",
        progress: 88,
        people: 125,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "88%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Integration Portfolio",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Connected service",
      "Client / tenant reference",
      "Authorized scopes",
      "Return URL",
      "Data owner",
      "Security review note",
    ],
    chart: [68, 75, 82, 89, 48, 55, 62, 69],
    external: { domain: "Organization-approved connected service", mode: "OAuth, webhook or signed event" },
    signature:
      "organization::integrations::catalogue::organization-connector-catalogue-and-data-scope-registry::v2",
  },
  "integrations-identity": {
    id: "integrations-identity",
    area: "integrations",
    areaLabel: "Integrations",
    title: "Identity & HRIS Integration",
    description:
      "Map identities, employment status, teams and allowed attributes into Academy membership without importing unrelated HR data.",
    group: "Identity Integration",
    icon: "key",
    layout: "import",
    model: "Identity and HRIS Mapping, Provisioning and Reconciliation Workflow",
    primaryAction: "Configure identity source",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Mapped fields", value: "USD 720K", trend: "Within approved threshold", icon: "key" },
      { label: "Provisioned", value: "231", trend: "Human review required", icon: "chart" },
      { label: "Exceptions", value: "UGX 184M", trend: "Updated today", icon: "check" },
      { label: "Deprovisioned", value: "4", trend: "On target", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Mapping / Job" },
      { key: "source", label: "Source" },
      { key: "scope", label: "Destination" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "INTEGRATIONS-IDENTITY-01",
        name: "Employee identifier mapping",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 75,
        people: 39,
        updated: "Today 09:22",
        role: "Owner",
        value: "USD 720K",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "75%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Identity Integration",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "INTEGRATIONS-IDENTITY-02",
        name: "Team and department mapping",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Pending",
        progress: 86,
        people: 56,
        updated: "Today 08:40",
        role: "Manager",
        value: "268",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "86%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "INTEGRATIONS-IDENTITY-03",
        name: "Employment status rule",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Healthy",
        progress: 54,
        people: 73,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "76%",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "54%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "INTEGRATIONS-IDENTITY-04",
        name: "SCIM role mapping",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "On track",
        progress: 65,
        people: 90,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "231",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "65%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "INTEGRATIONS-IDENTITY-05",
        name: "Deprovisioning policy",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Review due",
        progress: 76,
        people: 107,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "83%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "76%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [67, 74, 81, 88, 47, 54, 61, 68],
    external: { domain: "Organization-approved connected service", mode: "OAuth, webhook or signed event" },
    signature:
      "organization::integrations::identity::identity-and-hris-mapping,-provisioning-and-reconciliation-workflow::v2",
    accessRoles: ["owner", "people-admin", "technical-admin", "governance-lead", "auditor"],
    manageRoles: ["owner", "technical-admin", "people-admin"],
  },
  "integrations-learning": {
    id: "integrations-learning",
    area: "integrations",
    areaLabel: "Integrations",
    title: "LMS & Learning Data Integration",
    description:
      "Map programs, cohorts, assignments, completion and approved evidence between Academy and an organization-approved LMS.",
    group: "Learning Integration",
    icon: "book",
    layout: "map",
    model: "Learning Data Mapping and Synchronization Console",
    primaryAction: "Create learning mapping",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Mappings", value: "USD 720K", trend: "3 actions due", icon: "book" },
      { label: "Sync health", value: "231", trend: "Evidence current", icon: "chart" },
      { label: "Lag", value: "UGX 184M", trend: "Within approved threshold", icon: "check" },
      { label: "Exceptions", value: "4", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Outcome / Node" },
      { key: "scope", label: "Connected to" },
      { key: "owner", label: "Owner" },
      { key: "evidence", label: "Evidence" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "INTEGRATIONS-LEARNING-01",
        name: "Program and course mapping",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 75,
        people: 39,
        updated: "Today 09:22",
        role: "Owner",
        value: "USD 720K",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "75%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Learning Integration",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "INTEGRATIONS-LEARNING-02",
        name: "Cohort and enrolment sync",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Pending",
        progress: 86,
        people: 56,
        updated: "Today 08:40",
        role: "Manager",
        value: "268",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "86%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "INTEGRATIONS-LEARNING-03",
        name: "Completion and progress mapping",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Healthy",
        progress: 54,
        people: 73,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "76%",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "54%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "INTEGRATIONS-LEARNING-04",
        name: "Approved evidence callback",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "On track",
        progress: 65,
        people: 90,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "231",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "65%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "INTEGRATIONS-LEARNING-05",
        name: "Credential status mapping",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Review due",
        progress: 76,
        people: 107,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "83%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "76%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [67, 74, 81, 88, 47, 54, 61, 68],
    external: { domain: "Organization-approved connected service", mode: "OAuth, webhook or signed event" },
    signature: "organization::integrations::learning::learning-data-mapping-and-synchronization-console::v2",
  },
  "integrations-collaboration": {
    id: "integrations-collaboration",
    area: "integrations",
    areaLabel: "Integrations",
    title: "Collaboration & Calendar Integrations",
    description:
      "Configure organization-approved collaboration and calendar tools, delivery channels, scopes, consent, rate limits and health.",
    group: "Collaboration Integration",
    icon: "message",
    layout: "connection",
    model: "Collaboration, Calendar and Notification Connector Console",
    primaryAction: "Connect collaboration tool",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Connected tools", value: "12", trend: "Evidence current", icon: "message" },
      { label: "Channels", value: "68%", trend: "Within approved threshold", icon: "chart" },
      { label: "Delivery rate", value: "14", trend: "Human review required", icon: "check" },
      { label: "Scope reviews", value: "76%", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Connected service" },
      { key: "provider", label: "Provider" },
      { key: "scope", label: "Data / authority scope" },
      { key: "updated", label: "Last check" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "INTEGRATIONS-COLLABORATION-01",
        name: "Microsoft Teams connector",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Active",
        progress: 80,
        people: 44,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "80%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Collaboration Integration",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "INTEGRATIONS-COLLABORATION-02",
        name: "Google Calendar connector",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 91,
        people: 61,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "91%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "INTEGRATIONS-COLLABORATION-03",
        name: "Outlook notifications",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Attention",
        progress: 59,
        people: 78,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "59%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "INTEGRATIONS-COLLABORATION-04",
        name: "Slack project channel",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Approved",
        progress: 70,
        people: 95,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "70%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "INTEGRATIONS-COLLABORATION-05",
        name: "WhatsApp approval alerts",
        owner: "Sarah Atim",
        scope: "Operations team",
        status: "Pending",
        progress: 81,
        people: 112,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "On target",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "81%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Connected service",
      "Client / tenant reference",
      "Authorized scopes",
      "Return URL",
      "Data owner",
      "Security review note",
    ],
    chart: [72, 79, 86, 93, 52, 59, 66, 73],
    external: { domain: "Organization-approved connected service", mode: "OAuth, webhook or signed event" },
    signature:
      "organization::integrations::collaboration::collaboration,-calendar-and-notification-connector-console::v2",
  },
  "integrations-automation": {
    id: "integrations-automation",
    area: "integrations",
    areaLabel: "Integrations",
    title: "Automation & Webhooks",
    description:
      "Create signed event subscriptions, transformations, retries, replay, dead-letter handling, secrets and destination-specific scopes.",
    group: "Automation",
    icon: "code",
    layout: "board",
    model: "Webhook, Event and Automation Delivery Workbench",
    primaryAction: "Create webhook",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Subscriptions", value: "76%", trend: "+8% this cycle", icon: "code" },
      { label: "Events today", value: "12", trend: "3 actions due", icon: "chart" },
      { label: "Retries", value: "68%", trend: "Evidence current", icon: "check" },
      { label: "Dead-letter", value: "14", trend: "Within approved threshold", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Requirement" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "INTEGRATIONS-AUTOMATION-01",
        name: "Learner enrolled event",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Healthy",
        progress: 77,
        people: 41,
        updated: "Today 09:22",
        role: "Owner",
        value: "76%",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "77%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Automation",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "INTEGRATIONS-AUTOMATION-02",
        name: "Cohort capacity threshold event",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 88,
        people: 58,
        updated: "Today 08:40",
        role: "Manager",
        value: "231",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "88%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "INTEGRATIONS-AUTOMATION-03",
        name: "Invoice issued event",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "Review due",
        progress: 56,
        people: 75,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "83%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "56%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "INTEGRATIONS-AUTOMATION-04",
        name: "Risk escalation event",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Active",
        progress: 67,
        people: 92,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "12",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "67%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "INTEGRATIONS-AUTOMATION-05",
        name: "Credential issued event",
        owner: "Mariam Nansubuga",
        scope: "Executive committee",
        status: "Ready",
        progress: 78,
        people: 109,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "UGX 184M",
        target: "85%",
        level: "Optimized",
        trend: "Within approved threshold",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "78%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [69, 76, 83, 90, 49, 56, 63, 70],
    external: { domain: "Organization-approved connected service", mode: "OAuth, webhook or signed event" },
    signature: "organization::integrations::automation::webhook,-event-and-automation-delivery-workbench::v2",
    accessRoles: ["owner", "technical-admin", "governance-lead", "auditor"],
    manageRoles: ["owner", "technical-admin"],
  },
  "integrations-health": {
    id: "integrations-health",
    area: "integrations",
    areaLabel: "Integrations",
    title: "Integration Health & Diagnostics",
    description:
      "Monitor authentication, authorization, latency, schema compatibility, event lag, webhook delivery, retries, errors and recovery actions.",
    group: "Integration Operations",
    icon: "pulse",
    layout: "integration-health",
    model: "Integration Health, Lag and Recovery Command",
    primaryAction: "Run diagnostics",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Healthy", value: "92%", trend: "Human review required", icon: "pulse" },
      { label: "Degraded", value: "268", trend: "Updated today", icon: "chart" },
      { label: "Event lag", value: "83%", trend: "On target", icon: "check" },
      { label: "Recovery actions", value: "5", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Service / Connector" },
      { key: "provider", label: "Provider" },
      { key: "latency", label: "Latency / Lag" },
      { key: "updated", label: "Last check" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "INTEGRATIONS-HEALTH-01",
        name: "Identity provisioning",
        owner: "Mariam Nansubuga",
        scope: "Customer Experience",
        status: "Healthy",
        progress: 73,
        people: 37,
        updated: "Today 09:22",
        role: "Owner",
        value: "92%",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "73%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Integration Operations",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "INTEGRATIONS-HEALTH-02",
        name: "Learning data sync",
        owner: "David Kato",
        scope: "Technology & Data",
        status: "Healthy",
        progress: 84,
        people: 54,
        updated: "Today 08:40",
        role: "Manager",
        value: "14",
        target: "100%",
        level: "Established",
        trend: "Human review required",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "84%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "INTEGRATIONS-HEALTH-03",
        name: "Class attendance callback",
        owner: "Peter Ouma",
        scope: "Executive committee",
        status: "Review due",
        progress: 95,
        people: 71,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "USD 720K",
        target: "UGX 200M",
        level: "Developing",
        trend: "Updated today",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "95%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "INTEGRATIONS-HEALTH-04",
        name: "Labs evidence callback",
        owner: "Sarah Atim",
        scope: "October 2026 cohort",
        status: "Attention",
        progress: 63,
        people: 88,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "268",
        target: "0 exceptions",
        level: "Initial",
        trend: "On target",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "63%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "INTEGRATIONS-HEALTH-05",
        name: "Finance webhook delivery",
        owner: "Joseph Okello",
        scope: "Customer Experience",
        status: "Healthy",
        progress: 74,
        people: 105,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "76%",
        target: "85%",
        level: "Optimized",
        trend: "1 exception",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "74%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [65, 72, 79, 86, 93, 52, 59, 66],
    external: { domain: "Organization-approved connected service", mode: "OAuth, webhook or signed event" },
    signature: "organization::integrations::health::integration-health,-lag-and-recovery-command::v2",
    accessRoles: ["owner", "technical-admin", "governance-lead", "auditor"],
    manageRoles: ["owner", "technical-admin"],
  },
  "procurement-overview": {
    id: "procurement-overview",
    area: "procurement",
    areaLabel: "Procurement",
    title: "Procurement Command",
    description:
      "Monitor vendor due diligence, contracts, data protection, statements of work, purchase orders, approvals and renewal dependencies.",
    group: "Procurement",
    icon: "cart",
    layout: "dashboard",
    model: "Academy Procurement and Vendor Command Centre",
    primaryAction: "Create procurement action",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Open requests", value: "14", trend: "Updated today", icon: "cart" },
      { label: "Due diligence", value: "76%", trend: "On target", icon: "chart" },
      { label: "Approvals due", value: "12", trend: "1 exception", icon: "check" },
      { label: "Renewal dependencies", value: "68%", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Priority / Domain" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "status", label: "Status" },
      { key: "updated", label: "Updated" },
    ],
    rows: [
      {
        id: "PROCUREMENT-OVERVIEW-01",
        name: "Vendor due-diligence review",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 74,
        people: 38,
        updated: "Today 09:22",
        role: "Owner",
        value: "14",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "74%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Procurement",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROCUREMENT-OVERVIEW-02",
        name: "DPA signature dependency",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 85,
        people: 55,
        updated: "Today 08:40",
        role: "Manager",
        value: "USD 720K",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "85%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROCUREMENT-OVERVIEW-03",
        name: "Purchase-order approval",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 96,
        people: 72,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "268",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "96%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROCUREMENT-OVERVIEW-04",
        name: "Security evidence expiry",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 64,
        people: 89,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "76%",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "64%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PROCUREMENT-OVERVIEW-05",
        name: "Renewal commercial review",
        owner: "Ronald Isabirye",
        scope: "Finance & Procurement",
        status: "On track",
        progress: 75,
        people: 106,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "231",
        target: "85%",
        level: "Optimized",
        trend: "+8% this cycle",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "75%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [66, 73, 80, 87, 46, 53, 60, 67],
    external: null,
    signature: "organization::procurement::overview::academy-procurement-and-vendor-command-centre::v2",
    accessRoles: ["owner", "procurement-officer", "finance-admin", "governance-lead", "auditor"],
  },
  "procurement-documents": {
    id: "procurement-documents",
    area: "procurement",
    areaLabel: "Procurement",
    title: "Vendor & Security Documents",
    description:
      "Organize security evidence, insurance, tax, privacy, accessibility, responsible AI and vendor documents with expiry and review state.",
    group: "Due Diligence",
    icon: "file",
    layout: "library",
    model: "Vendor Security, Compliance and Due-Diligence Library",
    primaryAction: "Upload document",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Documents", value: "USD 720K", trend: "Updated today", icon: "file" },
      { label: "Current", value: "231", trend: "On target", icon: "chart" },
      { label: "Expiring", value: "UGX 184M", trend: "1 exception", icon: "check" },
      { label: "Missing", value: "4", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Item" },
      { key: "category", label: "Category" },
      { key: "language", label: "Language" },
      { key: "updated", label: "Updated / Expiry" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROCUREMENT-DOCUMENTS-01",
        name: "ISO 27001 certificate",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 75,
        people: 39,
        updated: "Today 09:22",
        role: "Owner",
        value: "USD 720K",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "75%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Due Diligence",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROCUREMENT-DOCUMENTS-02",
        name: "Penetration test summary",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Pending",
        progress: 86,
        people: 56,
        updated: "Today 08:40",
        role: "Manager",
        value: "268",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "86%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROCUREMENT-DOCUMENTS-03",
        name: "Accessibility conformance report",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Healthy",
        progress: 54,
        people: 73,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "76%",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "54%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROCUREMENT-DOCUMENTS-04",
        name: "Data retention statement",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "On track",
        progress: 65,
        people: 90,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "231",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "65%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PROCUREMENT-DOCUMENTS-05",
        name: "Insurance certificate",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Review due",
        progress: 76,
        people: 107,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "83%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "76%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [67, 74, 81, 88, 47, 54, 61, 68],
    external: null,
    signature:
      "organization::procurement::documents::vendor-security,-compliance-and-due-diligence-library::v2",
    accessRoles: ["owner", "procurement-officer", "governance-lead", "auditor"],
    manageRoles: ["owner", "procurement-officer"],
  },
  "procurement-contracts": {
    id: "procurement-contracts",
    area: "procurement",
    areaLabel: "Procurement",
    title: "Contracts, DPA & SOW",
    description:
      "Manage contract, DPA and SOW drafting, review, negotiation, signatures, obligations, milestones, amendments and termination.",
    group: "Commercial Records",
    icon: "briefcase",
    layout: "partnership",
    model: "Contract, Data Processing and Statement-of-Work Lifecycle",
    primaryAction: "Create contract record",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Active contracts", value: "USD 720K", trend: "3 actions due", icon: "briefcase" },
      { label: "Amendments", value: "231", trend: "Evidence current", icon: "chart" },
      { label: "Obligations due", value: "UGX 184M", trend: "Within approved threshold", icon: "check" },
      { label: "Signatures pending", value: "4", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Agreement / Partner" },
      { key: "owner", label: "Owner" },
      { key: "deadline", label: "Milestone / Expiry" },
      { key: "scope", label: "Scope" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROCUREMENT-CONTRACTS-01",
        name: "Master Services Agreement",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 75,
        people: 39,
        updated: "Today 09:22",
        role: "Owner",
        value: "USD 720K",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "75%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Commercial Records",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROCUREMENT-CONTRACTS-02",
        name: "Data Processing Agreement",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Pending",
        progress: 86,
        people: 56,
        updated: "Today 08:40",
        role: "Manager",
        value: "268",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "86%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROCUREMENT-CONTRACTS-03",
        name: "Statement of Work — 2026 Cohorts",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Healthy",
        progress: 54,
        people: 73,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "76%",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "54%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROCUREMENT-CONTRACTS-04",
        name: "Support Schedule",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "On track",
        progress: 65,
        people: 90,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "231",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "65%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PROCUREMENT-CONTRACTS-05",
        name: "Commercial Amendment 01",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Review due",
        progress: 76,
        people: 107,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "83%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "76%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [67, 74, 81, 88, 47, 54, 61, 68],
    external: null,
    signature:
      "organization::procurement::contracts::contract,-data-processing-and-statement-of-work-lifecycle::v2",
    accessRoles: ["owner", "procurement-officer", "finance-admin", "governance-lead", "auditor"],
    manageRoles: ["owner", "procurement-officer"],
    approveRoles: ["owner", "procurement-officer"],
  },
  "procurement-purchase-orders": {
    id: "procurement-purchase-orders",
    area: "procurement",
    areaLabel: "Procurement",
    title: "Purchase Orders",
    description:
      "Track approved purchase orders, line items, budget, invoice matching, remaining commitment, owner and delivery status.",
    group: "Purchasing",
    icon: "receipt",
    layout: "finance",
    model: "Purchase Order Commitment and Matching Ledger",
    primaryAction: "Create purchase order",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Committed", value: "UGX 184M", trend: "Updated today", icon: "receipt" },
      { label: "Invoiced", value: "4", trend: "On target", icon: "chart" },
      { label: "Remaining", value: "USD 720K", trend: "1 exception", icon: "check" },
      { label: "Exceptions", value: "231", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Financial record" },
      { key: "amount", label: "Amount" },
      { key: "date", label: "Date / Term" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROCUREMENT-PURCHASE-ORDERS-01",
        name: "PO-2026-041 Academy seats",
        owner: "Mariam Nansubuga",
        scope: "Customer Experience",
        status: "Ready",
        progress: 81,
        people: 45,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "81%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Purchasing",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROCUREMENT-PURCHASE-ORDERS-02",
        name: "PO-2026-057 Advanced Labs",
        owner: "David Kato",
        scope: "Technology & Data",
        status: "Attention",
        progress: 92,
        people: 62,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "Human review required",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "92%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROCUREMENT-PURCHASE-ORDERS-03",
        name: "PO-2026-061 Private cohort",
        owner: "Peter Ouma",
        scope: "Executive committee",
        status: "Approved",
        progress: 60,
        people: 79,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Updated today",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "60%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROCUREMENT-PURCHASE-ORDERS-04",
        name: "PO-2026-068 Accessibility services",
        owner: "Sarah Atim",
        scope: "October 2026 cohort",
        status: "Pending",
        progress: 71,
        people: 96,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "On target",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "71%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: ["Financial reference", "Amount", "Currency", "Purchase order", "Owner", "Reconciliation note"],
    chart: [73, 80, 87, 46, 53, 60, 67, 74],
    external: null,
    signature:
      "organization::procurement::purchase-orders::purchase-order-commitment-and-matching-ledger::v2",
    accessRoles: ["owner", "procurement-officer", "finance-admin", "auditor"],
    manageRoles: ["owner", "procurement-officer"],
    approveRoles: ["owner", "finance-admin"],
  },
  "procurement-approvals": {
    id: "procurement-approvals",
    area: "procurement",
    areaLabel: "Procurement",
    title: "Procurement Approval Workflow",
    description:
      "Route procurement requests through budget, security, privacy, legal, governance and executive approvals with segregation of duties.",
    group: "Approvals",
    icon: "check",
    layout: "queue",
    model: "Segregated Procurement Approval and Escalation Queue",
    primaryAction: "Start approval",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Pending approvals", value: "USD 720K", trend: "Human review required", icon: "check" },
      { label: "Security review", value: "231", trend: "Updated today", icon: "chart" },
      { label: "Finance review", value: "UGX 184M", trend: "On target", icon: "check" },
      { label: "Executive review", value: "4", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "deadline", label: "Due" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "PROCUREMENT-APPROVALS-01",
        name: "Private cohort purchase",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Urgent",
        progress: 75,
        people: 39,
        updated: "Today 09:22",
        role: "Owner",
        value: "USD 720K",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "75%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Approvals",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "PROCUREMENT-APPROVALS-02",
        name: "Advanced Labs entitlement",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Review due",
        progress: 86,
        people: 56,
        updated: "Today 08:40",
        role: "Manager",
        value: "268",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "86%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "PROCUREMENT-APPROVALS-03",
        name: "Contract amendment 01",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 54,
        people: 73,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "76%",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "54%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "PROCUREMENT-APPROVALS-04",
        name: "Data-region exception",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "Ready",
        progress: 65,
        people: 90,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "231",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "65%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "PROCUREMENT-APPROVALS-05",
        name: "Renewal commercial terms",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Urgent",
        progress: 76,
        people: 107,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "83%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "76%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [67, 74, 81, 88, 47, 54, 61, 68],
    external: null,
    signature:
      "organization::procurement::approvals::segregated-procurement-approval-and-escalation-queue::v2",
    accessRoles: ["owner", "procurement-officer", "finance-admin", "governance-lead", "auditor"],
    manageRoles: ["owner", "procurement-officer"],
    approveRoles: ["owner", "procurement-officer", "finance-admin", "governance-lead"],
  },
  "finance-overview": {
    id: "finance-overview",
    area: "finance",
    areaLabel: "Billing & Finance",
    title: "Finance Command",
    description:
      "Review plan value, invoiced amount, paid amount, outstanding liabilities, credits, usage, sponsorship and renewal assumptions.",
    group: "Finance",
    icon: "wallet",
    layout: "finance",
    model: "Academy Finance, Liability and Payment Command",
    primaryAction: "Open reconciliation",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Invoiced", value: "5", trend: "On target", icon: "wallet" },
      { label: "Paid", value: "92%", trend: "1 exception", icon: "chart" },
      { label: "Outstanding", value: "268", trend: "+8% this cycle", icon: "check" },
      { label: "Credits", value: "83%", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Financial record" },
      { key: "amount", label: "Amount" },
      { key: "date", label: "Date / Term" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "FINANCE-OVERVIEW-01",
        name: "October 2026 Academy invoice",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Finance",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "FINANCE-OVERVIEW-02",
        name: "Advanced Labs invoice",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "FINANCE-OVERVIEW-03",
        name: "Private cohort deposit",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "FINANCE-OVERVIEW-04",
        name: "Support service credit",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "FINANCE-OVERVIEW-05",
        name: "Outstanding purchase-order match",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Attention",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: ["Financial reference", "Amount", "Currency", "Purchase order", "Owner", "Reconciliation note"],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::finance::overview::academy-finance,-liability-and-payment-command::v2",
    accessRoles: ["owner", "finance-admin", "procurement-officer", "auditor"],
    manageRoles: ["owner", "finance-admin"],
  },
  "finance-plan": {
    id: "finance-plan",
    area: "finance",
    areaLabel: "Billing & Finance",
    title: "Plan, Seats & Commercial Terms",
    description:
      "Model contracted seats, assigned seats, program access, support, data region, term, unit pricing and approved plan changes.",
    group: "Plan",
    icon: "users",
    layout: "capacity",
    model: "Organization Plan, Seat and Commercial-Term Capacity Planner",
    primaryAction: "Change seat plan",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Contracted seats", value: "231", trend: "Human review required", icon: "users" },
      { label: "Assigned", value: "UGX 184M", trend: "Updated today", icon: "chart" },
      { label: "Available", value: "4", trend: "On target", icon: "check" },
      { label: "Projected need", value: "USD 720K", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Resource" },
      { key: "capacity", label: "Capacity" },
      { key: "usage", label: "Assigned / Used" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "FINANCE-PLAN-01",
        name: "Contracted Academy seats",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 66,
        people: 30,
        updated: "Today 09:22",
        role: "Owner",
        value: "231",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "66%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Plan",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "FINANCE-PLAN-02",
        name: "Assigned learner seats",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 77,
        people: 47,
        updated: "Today 08:40",
        role: "Manager",
        value: "83%",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "77%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "FINANCE-PLAN-03",
        name: "Advanced Labs seats",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 88,
        people: 64,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "12",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "88%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "FINANCE-PLAN-04",
        name: "Private cohort capacity",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 56,
        people: 81,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "UGX 184M",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "56%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "FINANCE-PLAN-05",
        name: "Organization support entitlement",
        owner: "Ronald Isabirye",
        scope: "Finance & Procurement",
        status: "On track",
        progress: 67,
        people: 98,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "5",
        target: "85%",
        level: "Optimized",
        trend: "+8% this cycle",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "67%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [58, 65, 72, 79, 86, 93, 52, 59],
    external: null,
    signature:
      "organization::finance::plan::organization-plan,-seat-and-commercial-term-capacity-planner::v2",
    accessRoles: ["owner", "finance-admin", "program-manager", "auditor"],
    manageRoles: ["owner", "finance-admin"],
    approveRoles: ["owner", "finance-admin"],
  },
  "finance-invoices": {
    id: "finance-invoices",
    area: "finance",
    areaLabel: "Billing & Finance",
    title: "Invoices, Payments & Credits",
    description:
      "Track invoice state, amount, currency, payment, credit note, dispute, purchase-order match and reconciliation evidence.",
    group: "Receivables",
    icon: "receipt",
    layout: "table",
    model: "Invoice, Payment, Credit and Reconciliation Register",
    primaryAction: "Record payment evidence",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Invoices", value: "5", trend: "Human review required", icon: "receipt" },
      { label: "Paid", value: "92%", trend: "Updated today", icon: "chart" },
      { label: "Pending", value: "268", trend: "On target", icon: "check" },
      { label: "Disputed", value: "83%", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Record / Event" },
      { key: "owner", label: "Actor / Owner" },
      { key: "scope", label: "Scope" },
      { key: "updated", label: "Time" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "FINANCE-INVOICES-01",
        name: "INV-CAA-2026-1042",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Receivables",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "FINANCE-INVOICES-02",
        name: "INV-CAA-2026-1088",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "FINANCE-INVOICES-03",
        name: "CRN-CAA-2026-0091",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "FINANCE-INVOICES-04",
        name: "INV-CAA-2026-1120",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::finance::invoices::invoice,-payment,-credit-and-reconciliation-register::v2",
    accessRoles: ["owner", "finance-admin", "procurement-officer", "auditor"],
    manageRoles: ["owner", "finance-admin"],
  },
  "finance-usage": {
    id: "finance-usage",
    area: "finance",
    areaLabel: "Billing & Finance",
    title: "Usage & Charge Attribution",
    description:
      "Analyze seats, Class, Labs, storage, support and optional service usage with allocation, thresholds and billing assumptions.",
    group: "Usage",
    icon: "chart",
    layout: "analytics",
    model: "Usage, Cost and Charge Attribution Studio",
    primaryAction: "Generate usage analysis",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Seat utilization", value: "83%", trend: "3 actions due", icon: "chart" },
      { label: "Class hours", value: "5", trend: "Evidence current", icon: "chart" },
      { label: "Labs usage", value: "92%", trend: "Within approved threshold", icon: "check" },
      { label: "Support hours", value: "268", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Measure" },
      { key: "value", label: "Value" },
      { key: "trend", label: "Trend" },
      { key: "source", label: "Evidence source" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "FINANCE-USAGE-01",
        name: "Active learner seats",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 67,
        people: 31,
        updated: "Today 09:22",
        role: "Owner",
        value: "83%",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "67%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Usage",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "FINANCE-USAGE-02",
        name: "Chatoner Class hours",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Pending",
        progress: 78,
        people: 48,
        updated: "Today 08:40",
        role: "Manager",
        value: "12",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "78%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "FINANCE-USAGE-03",
        name: "AI Labs run time",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Healthy",
        progress: 89,
        people: 65,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "UGX 184M",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "89%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "FINANCE-USAGE-04",
        name: "Evidence storage",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "On track",
        progress: 57,
        people: 82,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "5",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "57%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "FINANCE-USAGE-05",
        name: "Priority support hours",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Review due",
        progress: 68,
        people: 99,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "68%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "68%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [59, 66, 73, 80, 87, 46, 53, 60],
    external: null,
    signature: "organization::finance::usage::usage,-cost-and-charge-attribution-studio::v2",
    accessRoles: ["owner", "finance-admin", "program-manager", "auditor"],
    manageRoles: ["owner", "finance-admin"],
  },
  "finance-renewal": {
    id: "finance-renewal",
    area: "finance",
    areaLabel: "Billing & Finance",
    title: "Renewal Planning",
    description:
      "Prepare renewal decisions using realized value, adoption, usage, support, risk, capacity, terms, budget and named approval.",
    group: "Renewal",
    icon: "calendar",
    layout: "scorecard",
    model: "Renewal Scenario, Value and Approval Planner",
    primaryAction: "Create renewal scenario",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Scenarios", value: "UGX 184M", trend: "Human review required", icon: "calendar" },
      { label: "Preferred", value: "4", trend: "Updated today", icon: "chart" },
      { label: "Budget gap", value: "USD 720K", trend: "On target", icon: "check" },
      { label: "Approval gates", value: "231", trend: "1 exception", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Domain" },
      { key: "value", label: "Current" },
      { key: "target", label: "Target" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "FINANCE-RENEWAL-01",
        name: "Maintain current plan",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Healthy",
        progress: 69,
        people: 33,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "69%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Renewal",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "FINANCE-RENEWAL-02",
        name: "Expand builder capacity",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 80,
        people: 50,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "80%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "FINANCE-RENEWAL-03",
        name: "Add governance cohort",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "Review due",
        progress: 91,
        people: 67,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "91%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "FINANCE-RENEWAL-04",
        name: "Reduce dormant seats",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Active",
        progress: 59,
        people: 84,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "59%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "FINANCE-RENEWAL-05",
        name: "Multi-year agreement",
        owner: "Mariam Nansubuga",
        scope: "Executive committee",
        status: "Ready",
        progress: 70,
        people: 101,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "92%",
        target: "85%",
        level: "Optimized",
        trend: "Within approved threshold",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "70%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [61, 68, 75, 82, 89, 48, 55, 62],
    external: null,
    signature: "organization::finance::renewal::renewal-scenario,-value-and-approval-planner::v2",
    accessRoles: ["owner", "finance-admin", "program-manager", "procurement-officer", "auditor"],
    manageRoles: ["owner", "finance-admin"],
    approveRoles: ["owner", "finance-admin"],
  },
  "reports-library": {
    id: "reports-library",
    area: "reports",
    areaLabel: "Reports & Exports",
    title: "Report Library",
    description:
      "Browse scheduled and generated executive, learning, adoption, governance, finance, support and audit reports with classification and access.",
    group: "Reports",
    icon: "library",
    layout: "library",
    model: "Role-Aware Organization Report Library",
    primaryAction: "Create report",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Available reports", value: "UGX 184M", trend: "On target", icon: "library" },
      { label: "Scheduled", value: "4", trend: "1 exception", icon: "chart" },
      { label: "Restricted", value: "USD 720K", trend: "+8% this cycle", icon: "check" },
      { label: "Recently generated", value: "231", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Item" },
      { key: "category", label: "Category" },
      { key: "language", label: "Language" },
      { key: "updated", label: "Updated / Expiry" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "REPORTS-LIBRARY-01",
        name: "Executive Capability Report",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Healthy",
        progress: 69,
        people: 33,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "69%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Reports",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "REPORTS-LIBRARY-02",
        name: "Learning and Credential Report",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "On track",
        progress: 80,
        people: 50,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "80%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "REPORTS-LIBRARY-03",
        name: "Adoption and Barrier Report",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "Review due",
        progress: 91,
        people: 67,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "91%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "REPORTS-LIBRARY-04",
        name: "Governance and Risk Report",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Active",
        progress: 59,
        people: 84,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "59%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "REPORTS-LIBRARY-05",
        name: "Finance and Usage Report",
        owner: "Mariam Nansubuga",
        scope: "Executive committee",
        status: "Ready",
        progress: 70,
        people: 101,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "92%",
        target: "85%",
        level: "Optimized",
        trend: "Within approved threshold",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "70%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [61, 68, 75, 82, 89, 48, 55, 62],
    external: null,
    signature: "organization::reports::library::role-aware-organization-report-library::v2",
  },
  "reports-executive": {
    id: "reports-executive",
    area: "reports",
    areaLabel: "Reports & Exports",
    title: "Executive Capability Report",
    description:
      "Generate a leadership report combining readiness, adoption, learning, projects, governance, finance and evidence-backed value.",
    group: "Executive Reporting",
    icon: "chart",
    layout: "analytics",
    model: "Executive Capability and Value Reporting Studio",
    primaryAction: "Generate executive report",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Readiness", value: "68%", trend: "1 exception", icon: "chart" },
      { label: "Adoption", value: "14", trend: "+8% this cycle", icon: "chart" },
      { label: "Value claims", value: "76%", trend: "3 actions due", icon: "check" },
      { label: "Open decisions", value: "12", trend: "Evidence current", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Measure" },
      { key: "value", label: "Value" },
      { key: "trend", label: "Trend" },
      { key: "source", label: "Evidence source" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "REPORTS-EXECUTIVE-01",
        name: "Capability readiness",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Executive Reporting",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "REPORTS-EXECUTIVE-02",
        name: "Learning delivery",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "REPORTS-EXECUTIVE-03",
        name: "Responsible adoption",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "REPORTS-EXECUTIVE-04",
        name: "Project outcomes",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "REPORTS-EXECUTIVE-05",
        name: "Governance coverage",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
      {
        id: "REPORTS-EXECUTIVE-06",
        name: "Commercial position",
        owner: "Sarah Atim",
        scope: "Customer Experience",
        status: "Pending",
        progress: 83,
        people: 120,
        updated: "Today 09:22",
        role: "Owner",
        value: "268",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "83%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Executive Reporting",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature: "organization::reports::executive::executive-capability-and-value-reporting-studio::v2",
  },
  "reports-learning": {
    id: "reports-learning",
    area: "reports",
    areaLabel: "Reports & Exports",
    title: "Learning & Credential Report",
    description:
      "Report enrolment, participation, completion, support, assessment and credential readiness using privacy thresholds and role scope.",
    group: "Learning Reporting",
    icon: "book",
    layout: "analytics",
    model: "Learning Delivery and Credential Reporting Studio",
    primaryAction: "Generate learning report",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Activation", value: "5", trend: "3 actions due", icon: "book" },
      { label: "Completion", value: "92%", trend: "Evidence current", icon: "chart" },
      { label: "Support rate", value: "268", trend: "Within approved threshold", icon: "check" },
      { label: "Credential readiness", value: "83%", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Measure" },
      { key: "value", label: "Value" },
      { key: "trend", label: "Trend" },
      { key: "source", label: "Evidence source" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "REPORTS-LEARNING-01",
        name: "Enrolment and activation",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Learning Reporting",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "REPORTS-LEARNING-02",
        name: "Participation and attendance",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "REPORTS-LEARNING-03",
        name: "Completion and support",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "REPORTS-LEARNING-04",
        name: "Assessment throughput",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "REPORTS-LEARNING-05",
        name: "Credential readiness",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Attention",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::reports::learning::learning-delivery-and-credential-reporting-studio::v2",
  },
  "reports-adoption": {
    id: "reports-adoption",
    area: "reports",
    areaLabel: "Reports & Exports",
    title: "Adoption & Barrier Report",
    description:
      "Compare team adoption, barriers, interventions, support actions and change over time without ranking individual learners.",
    group: "Adoption Reporting",
    icon: "pulse",
    layout: "scorecard",
    model: "Team Adoption, Barrier and Intervention Report",
    primaryAction: "Generate adoption report",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Adoption", value: "5", trend: "On target", icon: "pulse" },
      { label: "Teams measured", value: "92%", trend: "1 exception", icon: "chart" },
      { label: "Barriers", value: "268", trend: "+8% this cycle", icon: "check" },
      { label: "Interventions", value: "83%", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Domain" },
      { key: "value", label: "Current" },
      { key: "target", label: "Target" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "REPORTS-ADOPTION-01",
        name: "Operations adoption",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Adoption Reporting",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "REPORTS-ADOPTION-02",
        name: "Customer Experience adoption",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "REPORTS-ADOPTION-03",
        name: "Finance adoption",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "REPORTS-ADOPTION-04",
        name: "Technology adoption",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "REPORTS-ADOPTION-05",
        name: "People and Culture adoption",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Attention",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature: "organization::reports::adoption::team-adoption,-barrier-and-intervention-report::v2",
  },
  "reports-governance": {
    id: "reports-governance",
    area: "reports",
    areaLabel: "Reports & Exports",
    title: "Governance & Risk Report",
    description:
      "Report policy status, approved tools, risk treatment, acknowledgements, access reviews, incidents and audit evidence.",
    group: "Governance Reporting",
    icon: "shield",
    layout: "scorecard",
    model: "Governance, Risk and Control Effectiveness Report",
    primaryAction: "Generate governance report",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Control coverage", value: "4", trend: "3 actions due", icon: "shield" },
      { label: "High risks", value: "USD 720K", trend: "Evidence current", icon: "chart" },
      { label: "Acknowledgements", value: "231", trend: "Within approved threshold", icon: "check" },
      { label: "Audit exceptions", value: "UGX 184M", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Domain" },
      { key: "value", label: "Current" },
      { key: "target", label: "Target" },
      { key: "owner", label: "Owner" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "REPORTS-GOVERNANCE-01",
        name: "Policy coverage",
        owner: "Grace Namuli",
        scope: "Operations team",
        status: "Active",
        progress: 72,
        people: 36,
        updated: "Today 09:22",
        role: "Owner",
        value: "4",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "72%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Governance Reporting",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "REPORTS-GOVERNANCE-02",
        name: "Approved tool compliance",
        owner: "Mariam Nansubuga",
        scope: "Finance & Procurement",
        status: "Ready",
        progress: 83,
        people: 53,
        updated: "Today 08:40",
        role: "Manager",
        value: "92%",
        target: "100%",
        level: "Established",
        trend: "Within approved threshold",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "83%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "REPORTS-GOVERNANCE-03",
        name: "Risk treatment",
        owner: "David Kato",
        scope: "People & Culture",
        status: "Attention",
        progress: 94,
        people: 70,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "14",
        target: "UGX 200M",
        level: "Developing",
        trend: "Human review required",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "94%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "REPORTS-GOVERNANCE-04",
        name: "Acknowledgement completion",
        owner: "Peter Ouma",
        scope: "Organization-wide",
        status: "Approved",
        progress: 62,
        people: 87,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "USD 720K",
        target: "0 exceptions",
        level: "Initial",
        trend: "Updated today",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "62%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "REPORTS-GOVERNANCE-05",
        name: "Access certification",
        owner: "Sarah Atim",
        scope: "Operations team",
        status: "Pending",
        progress: 73,
        people: 104,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "268",
        target: "85%",
        level: "Optimized",
        trend: "On target",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "73%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [64, 71, 78, 85, 92, 51, 58, 65],
    external: null,
    signature: "organization::reports::governance::governance,-risk-and-control-effectiveness-report::v2",
  },
  "reports-custom": {
    id: "reports-custom",
    area: "reports",
    areaLabel: "Reports & Exports",
    title: "Custom Report Builder",
    description:
      "Select authorized measures, filters, date ranges, language, classification, recipients, schedule and human approval for a custom export.",
    group: "Report Builder",
    icon: "sliders",
    layout: "export",
    model: "Governed Custom Report and Export Builder",
    primaryAction: "Build custom report",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Measures", value: "12", trend: "3 actions due", icon: "sliders" },
      { label: "Data domains", value: "68%", trend: "Evidence current", icon: "chart" },
      { label: "Recipients", value: "14", trend: "Within approved threshold", icon: "check" },
      { label: "Approval gates", value: "76%", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Report component" },
      { key: "scope", label: "Data scope" },
      { key: "format", label: "Format" },
      { key: "owner", label: "Approver" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "REPORTS-CUSTOM-01",
        name: "Audience and purpose",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Pending",
        progress: 68,
        people: 32,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "68%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Report Builder",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "REPORTS-CUSTOM-02",
        name: "Authorized measures",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 79,
        people: 49,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "79%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "REPORTS-CUSTOM-03",
        name: "Date and organization scope",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "On track",
        progress: 90,
        people: 66,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "90%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "REPORTS-CUSTOM-04",
        name: "Language and format",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 58,
        people: 83,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "58%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "REPORTS-CUSTOM-05",
        name: "Classification and approval",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Active",
        progress: 69,
        people: 100,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "69%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: ["Report name", "Audience", "Data scope", "Date range", "Format", "Classification", "Approver"],
    chart: [60, 67, 74, 81, 88, 47, 54, 61],
    external: null,
    signature: "organization::reports::custom::governed-custom-report-and-export-builder::v2",
  },
  "support-success-plan": {
    id: "support-success-plan",
    area: "support",
    areaLabel: "Support & Success",
    title: "Organization Success Plan",
    description:
      "Manage agreed outcomes, milestones, adoption interventions, executive checkpoints, owners, due dates and evidence of success.",
    group: "Success",
    icon: "target",
    layout: "board",
    model: "Organization Capability Success Plan and Outcome Board",
    primaryAction: "Add success action",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Outcomes", value: "14", trend: "On target", icon: "target" },
      { label: "On track", value: "76%", trend: "1 exception", icon: "chart" },
      { label: "At risk", value: "12", trend: "+8% this cycle", icon: "check" },
      { label: "Due this month", value: "68%", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Requirement" },
      { key: "progress", label: "Progress" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SUPPORT-SUCCESS-PLAN-01",
        name: "Reach 80% program activation",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 74,
        people: 38,
        updated: "Today 09:22",
        role: "Owner",
        value: "14",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "74%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Success",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SUPPORT-SUCCESS-PLAN-02",
        name: "Complete five responsible pilots",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Approved",
        progress: 85,
        people: 55,
        updated: "Today 08:40",
        role: "Manager",
        value: "USD 720K",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "85%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SUPPORT-SUCCESS-PLAN-03",
        name: "Certify privileged Academy access",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Pending",
        progress: 96,
        people: 72,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "268",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "96%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SUPPORT-SUCCESS-PLAN-04",
        name: "Improve team adoption to 78%",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Healthy",
        progress: 64,
        people: 89,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "76%",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "64%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SUPPORT-SUCCESS-PLAN-05",
        name: "Prepare 2027 renewal decision",
        owner: "Ronald Isabirye",
        scope: "Finance & Procurement",
        status: "On track",
        progress: 75,
        people: 106,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "231",
        target: "85%",
        level: "Optimized",
        trend: "+8% this cycle",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "75%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [66, 73, 80, 87, 46, 53, 60, 67],
    external: null,
    signature:
      "organization::support::success-plan::organization-capability-success-plan-and-outcome-board::v2",
  },
  "support-tickets": {
    id: "support-tickets",
    area: "support",
    areaLabel: "Support & Success",
    title: "Support Tickets & Escalations",
    description:
      "Track technical, learning, accessibility, finance, governance and integration requests with severity, SLA, owner and resolution.",
    group: "Support",
    icon: "inbox",
    layout: "queue",
    model: "Organization Support Ticket and Escalation Queue",
    primaryAction: "Create support ticket",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Open tickets", value: "UGX 184M", trend: "+8% this cycle", icon: "inbox" },
      { label: "High priority", value: "4", trend: "3 actions due", icon: "chart" },
      { label: "Within SLA", value: "USD 720K", trend: "Evidence current", icon: "check" },
      { label: "Escalated", value: "231", trend: "Within approved threshold", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "deadline", label: "Due" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SUPPORT-TICKETS-01",
        name: "SCIM member mismatch",
        owner: "Joseph Okello",
        scope: "Executive committee",
        status: "Urgent",
        progress: 69,
        people: 33,
        updated: "Today 09:22",
        role: "Owner",
        value: "UGX 184M",
        target: "90%",
        level: "Advanced",
        trend: "1 exception",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "69%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Support",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SUPPORT-TICKETS-02",
        name: "Class caption language issue",
        owner: "Ronald Isabirye",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 80,
        people: 50,
        updated: "Today 08:40",
        role: "Manager",
        value: "5",
        target: "100%",
        level: "Established",
        trend: "+8% this cycle",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "80%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SUPPORT-TICKETS-03",
        name: "Labs evidence callback delayed",
        owner: "Amina Mutesi",
        scope: "Customer Experience",
        status: "On track",
        progress: 91,
        people: 67,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "68%",
        target: "UGX 200M",
        level: "Developing",
        trend: "3 actions due",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "91%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SUPPORT-TICKETS-04",
        name: "Invoice PO mismatch",
        owner: "Grace Namuli",
        scope: "Technology & Data",
        status: "Ready",
        progress: 59,
        people: 84,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "4",
        target: "0 exceptions",
        level: "Initial",
        trend: "Evidence current",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "59%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SUPPORT-TICKETS-05",
        name: "Governance report export request",
        owner: "Mariam Nansubuga",
        scope: "Executive committee",
        status: "Urgent",
        progress: 70,
        people: 101,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "92%",
        target: "85%",
        level: "Optimized",
        trend: "Within approved threshold",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "70%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [61, 68, 75, 82, 89, 48, 55, 62],
    external: null,
    signature: "organization::support::tickets::organization-support-ticket-and-escalation-queue::v2",
  },
  "support-contacts": {
    id: "support-contacts",
    area: "support",
    areaLabel: "Support & Success",
    title: "Success Team & Contacts",
    description:
      "Maintain organization contacts and assigned Academy success, support, finance, governance, technical and executive contacts.",
    group: "People",
    icon: "users",
    layout: "directory",
    model: "Academy Success, Support and Escalation Contact Directory",
    primaryAction: "Add organization contact",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Organization contacts", value: "5", trend: "3 actions due", icon: "users" },
      { label: "Academy contacts", value: "92%", trend: "Evidence current", icon: "chart" },
      { label: "Escalation paths", value: "268", trend: "Within approved threshold", icon: "check" },
      { label: "Coverage", value: "83%", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "role", label: "Role / Type" },
      { key: "scope", label: "Team / Scope" },
      { key: "status", label: "Status" },
      { key: "updated", label: "Last activity" },
    ],
    rows: [
      {
        id: "SUPPORT-CONTACTS-01",
        name: "Lydia Namanya",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "On track",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "People",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SUPPORT-CONTACTS-02",
        name: "Robert Chen",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Review due",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SUPPORT-CONTACTS-03",
        name: "Mariam Nansubuga",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Active",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SUPPORT-CONTACTS-04",
        name: "David Kato",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Ready",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SUPPORT-CONTACTS-05",
        name: "Grace Namuli",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Attention",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature:
      "organization::support::contacts::academy-success,-support-and-escalation-contact-directory::v2",
  },
  "support-resources": {
    id: "support-resources",
    area: "support",
    areaLabel: "Support & Success",
    title: "Organization Resource Library",
    description:
      "Organize approved playbooks, policies, templates, recordings, guides, FAQs and adoption materials by audience and language.",
    group: "Resources",
    icon: "folder",
    layout: "library",
    model: "Organization Enablement, Adoption and Support Resource Library",
    primaryAction: "Upload resource",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Resources", value: "68%", trend: "On target", icon: "folder" },
      { label: "Languages", value: "14", trend: "1 exception", icon: "chart" },
      { label: "Recently updated", value: "76%", trend: "+8% this cycle", icon: "check" },
      { label: "Restricted", value: "12", trend: "3 actions due", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Item" },
      { key: "category", label: "Category" },
      { key: "language", label: "Language" },
      { key: "updated", label: "Updated / Expiry" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SUPPORT-RESOURCES-01",
        name: "Organization launch playbook",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Resources",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SUPPORT-RESOURCES-02",
        name: "Team manager adoption guide",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SUPPORT-RESOURCES-03",
        name: "Responsible AI policy template",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SUPPORT-RESOURCES-04",
        name: "Class and Labs handoff guide",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SUPPORT-RESOURCES-05",
        name: "Credential evidence guide",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature:
      "organization::support::resources::organization-enablement,-adoption-and-support-resource-library::v2",
  },
  "support-service-status": {
    id: "support-service-status",
    area: "support",
    areaLabel: "Support & Success",
    title: "Service Status & Maintenance",
    description:
      "Monitor Academy, Class, Labs, handoff, notification and integration status, maintenance, incidents and resolved service history.",
    group: "Service Health",
    icon: "pulse",
    layout: "integration-health",
    model: "Academy, Class and Labs Service Status Centre",
    primaryAction: "Subscribe to updates",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Services", value: "268", trend: "Updated today", icon: "pulse" },
      { label: "Operational", value: "83%", trend: "On target", icon: "chart" },
      { label: "Degraded", value: "5", trend: "1 exception", icon: "check" },
      { label: "Incidents", value: "92%", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Service / Connector" },
      { key: "provider", label: "Provider" },
      { key: "latency", label: "Latency / Lag" },
      { key: "updated", label: "Last check" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SUPPORT-SERVICE-STATUS-01",
        name: "Chatoner AI Academy",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Healthy",
        progress: 76,
        people: 40,
        updated: "Today 09:22",
        role: "Owner",
        value: "268",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "76%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Service Health",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SUPPORT-SERVICE-STATUS-02",
        name: "Chatoner Class",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 87,
        people: 57,
        updated: "Today 08:40",
        role: "Manager",
        value: "76%",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "87%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SUPPORT-SERVICE-STATUS-03",
        name: "Chatoner AI Labs",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "Review due",
        progress: 55,
        people: 74,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "231",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "55%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SUPPORT-SERVICE-STATUS-04",
        name: "Identity provisioning",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Attention",
        progress: 66,
        people: 91,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "83%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "66%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SUPPORT-SERVICE-STATUS-05",
        name: "Notification delivery",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Healthy",
        progress: 77,
        people: 108,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "12",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "77%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [68, 75, 82, 89, 48, 55, 62, 69],
    external: null,
    signature: "organization::support::service-status::academy,-class-and-labs-service-status-centre::v2",
  },
  "search-global": {
    id: "search-global",
    area: "search",
    areaLabel: "Academy Search",
    title: "Organization Universal Search",
    description:
      "Search authorized people, programs, cohorts, Classes, Labs, Build Rooms, projects, policies, procurement, finance, reports and support records.",
    group: "Search",
    icon: "search",
    layout: "directory",
    model: "Cross-Workspace Organization Search and Discovery",
    primaryAction: "Save search",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Indexed records", value: "83%", trend: "3 actions due", icon: "search" },
      { label: "Authorized areas", value: "5", trend: "Evidence current", icon: "chart" },
      { label: "Saved searches", value: "92%", trend: "Within approved threshold", icon: "check" },
      { label: "Recent results", value: "268", trend: "Human review required", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Name" },
      { key: "role", label: "Role / Type" },
      { key: "scope", label: "Team / Scope" },
      { key: "status", label: "Status" },
      { key: "updated", label: "Last activity" },
    ],
    rows: [
      {
        id: "SEARCH-GLOBAL-01",
        name: "AI Product Builder October cohort",
        owner: "Peter Ouma",
        scope: "Technology & Data",
        status: "Approved",
        progress: 67,
        people: 31,
        updated: "Today 09:22",
        role: "Owner",
        value: "83%",
        target: "90%",
        level: "Advanced",
        trend: "Updated today",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "67%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Search",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SEARCH-GLOBAL-02",
        name: "Ronald Isabirye",
        owner: "Sarah Atim",
        scope: "Executive committee",
        status: "Pending",
        progress: 78,
        people: 48,
        updated: "Today 08:40",
        role: "Manager",
        value: "12",
        target: "100%",
        level: "Established",
        trend: "On target",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "78%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SEARCH-GLOBAL-03",
        name: "Kijani Build Room",
        owner: "Joseph Okello",
        scope: "October 2026 cohort",
        status: "Healthy",
        progress: 89,
        people: 65,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "UGX 184M",
        target: "UGX 200M",
        level: "Developing",
        trend: "1 exception",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "89%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SEARCH-GLOBAL-04",
        name: "Responsible AI Use Policy",
        owner: "Ronald Isabirye",
        scope: "Customer Experience",
        status: "On track",
        progress: 57,
        people: 82,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "5",
        target: "0 exceptions",
        level: "Initial",
        trend: "+8% this cycle",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "57%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SEARCH-GLOBAL-05",
        name: "INV-CAA-2026-1042",
        owner: "Amina Mutesi",
        scope: "Technology & Data",
        status: "Review due",
        progress: 68,
        people: 99,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "68%",
        target: "85%",
        level: "Optimized",
        trend: "3 actions due",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "68%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
      {
        id: "SEARCH-GLOBAL-06",
        name: "SCIM member mismatch",
        owner: "Grace Namuli",
        scope: "Executive committee",
        status: "Active",
        progress: 79,
        people: 116,
        updated: "Today 09:22",
        role: "Owner",
        value: "4",
        target: "90%",
        level: "Advanced",
        trend: "Evidence current",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "79%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Search",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [59, 66, 73, 80, 87, 46, 53, 60],
    external: null,
    signature: "organization::search::global::cross-workspace-organization-search-and-discovery::v2",
  },
  "notifications-inbox": {
    id: "notifications-inbox",
    area: "notifications",
    areaLabel: "Notifications",
    title: "Organization Notification Centre",
    description:
      "Review actionable alerts from learning, Class, Labs, Rooms, projects, governance, finance, procurement, integrations and support.",
    group: "Notifications",
    icon: "bell",
    layout: "queue",
    model: "Role-Aware Organization Notification and Action Inbox",
    primaryAction: "Create notification rule",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Unread", value: "92%", trend: "Updated today", icon: "bell" },
      { label: "Action required", value: "268", trend: "On target", icon: "chart" },
      { label: "Deadlines", value: "83%", trend: "1 exception", icon: "check" },
      { label: "System", value: "5", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Work item" },
      { key: "owner", label: "Owner" },
      { key: "scope", label: "Scope" },
      { key: "deadline", label: "Due" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "NOTIFICATIONS-INBOX-01",
        name: "Instructor review condition",
        owner: "Mariam Nansubuga",
        scope: "Customer Experience",
        status: "Urgent",
        progress: 73,
        people: 37,
        updated: "Today 09:22",
        role: "Owner",
        value: "92%",
        target: "90%",
        level: "Advanced",
        trend: "Within approved threshold",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "73%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Notifications",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "NOTIFICATIONS-INBOX-02",
        name: "Cohort capacity threshold",
        owner: "David Kato",
        scope: "Technology & Data",
        status: "Review due",
        progress: 84,
        people: 54,
        updated: "Today 08:40",
        role: "Manager",
        value: "14",
        target: "100%",
        level: "Established",
        trend: "Human review required",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "84%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "NOTIFICATIONS-INBOX-03",
        name: "High-risk tool review due",
        owner: "Peter Ouma",
        scope: "Executive committee",
        status: "On track",
        progress: 95,
        people: 71,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "USD 720K",
        target: "UGX 200M",
        level: "Developing",
        trend: "Updated today",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "95%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "NOTIFICATIONS-INBOX-04",
        name: "Invoice payment recorded",
        owner: "Sarah Atim",
        scope: "October 2026 cohort",
        status: "Ready",
        progress: 63,
        people: 88,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "268",
        target: "0 exceptions",
        level: "Initial",
        trend: "On target",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "63%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "NOTIFICATIONS-INBOX-05",
        name: "Labs evidence callback recovered",
        owner: "Joseph Okello",
        scope: "Customer Experience",
        status: "Urgent",
        progress: 74,
        people: 105,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "76%",
        target: "85%",
        level: "Optimized",
        trend: "1 exception",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "74%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [65, 72, 79, 86, 93, 52, 59, 66],
    external: null,
    signature:
      "organization::notifications::inbox::role-aware-organization-notification-and-action-inbox::v2",
  },
  "settings-profile": {
    id: "settings-profile",
    area: "settings",
    areaLabel: "Organization Settings",
    title: "Organization Profile",
    description:
      "Configure organization identity, legal name, addresses, primary contacts, sector, countries, time zones and Academy operating context.",
    group: "Organization",
    icon: "building",
    layout: "form",
    model: "Organization Identity, Contacts and Operating Profile",
    primaryAction: "Save profile",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Legal entities", value: "—", trend: "Not reported", icon: "building" },
      { label: "Primary contacts", value: "—", trend: "Not reported", icon: "chart" },
      { label: "Countries", value: "—", trend: "Not reported", icon: "check" },
      { label: "Time zones", value: "—", trend: "Not reported", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Configuration field" },
      { key: "value", label: "Value" },
      { key: "owner", label: "Owner" },
      { key: "updated", label: "Updated" },
      { key: "status", label: "Status" },
    ],
    rows: [],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Organization display name",
      "Legal name",
      "Primary contact",
      "Country and time zone",
      "Default language",
      "Approved change reason",
    ],
    chart: [],
    external: null,
    signature: "organization::settings::profile::organization-identity,-contacts-and-operating-profile::v2",
  },
  "settings-preferences": {
    id: "settings-preferences",
    area: "settings",
    areaLabel: "Organization Settings",
    title: "Workspace Preferences",
    description:
      "Configure default languages, time zones, date formats, notification channels, quiet hours, accessibility and report preferences.",
    group: "Experience",
    icon: "sliders",
    layout: "policy",
    model: "Organization Workspace, Language and Notification Preferences",
    primaryAction: "Save preferences",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Languages", value: "14", trend: "Updated today", icon: "sliders" },
      { label: "Channels", value: "76%", trend: "On target", icon: "chart" },
      { label: "Quiet hours", value: "12", trend: "1 exception", icon: "check" },
      { label: "Accessibility", value: "68%", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Policy / Control" },
      { key: "version", label: "Version" },
      { key: "owner", label: "Owner" },
      { key: "deadline", label: "Review date" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SETTINGS-PREFERENCES-01",
        name: "Primary language",
        owner: "David Kato",
        scope: "Finance & Procurement",
        status: "Approved",
        progress: 74,
        people: 38,
        updated: "Today 09:22",
        role: "Owner",
        value: "14",
        target: "90%",
        level: "Advanced",
        trend: "Human review required",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "74%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Experience",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SETTINGS-PREFERENCES-02",
        name: "Secondary translation",
        owner: "Peter Ouma",
        scope: "People & Culture",
        status: "Active",
        progress: 85,
        people: 55,
        updated: "Today 08:40",
        role: "Manager",
        value: "USD 720K",
        target: "100%",
        level: "Established",
        trend: "Updated today",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "85%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SETTINGS-PREFERENCES-03",
        name: "Organization time zone",
        owner: "Sarah Atim",
        scope: "Organization-wide",
        status: "Review due",
        progress: 96,
        people: 72,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "268",
        target: "UGX 200M",
        level: "Developing",
        trend: "On target",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "96%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SETTINGS-PREFERENCES-04",
        name: "Notification quiet hours",
        owner: "Joseph Okello",
        scope: "Operations team",
        status: "Pending",
        progress: 64,
        people: 89,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "76%",
        target: "0 exceptions",
        level: "Initial",
        trend: "1 exception",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "64%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SETTINGS-PREFERENCES-05",
        name: "Accessibility defaults",
        owner: "Ronald Isabirye",
        scope: "Finance & Procurement",
        status: "Approved",
        progress: 75,
        people: 106,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "231",
        target: "85%",
        level: "Optimized",
        trend: "+8% this cycle",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "75%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Policy or control name",
      "Version",
      "Owner",
      "Effective date",
      "Review date",
      "Approval reason",
    ],
    chart: [66, 73, 80, 87, 46, 53, 60, 67],
    external: null,
    signature:
      "organization::settings::preferences::organization-workspace,-language-and-notification-preferences::v2",
  },
  "settings-privacy": {
    id: "settings-privacy",
    area: "settings",
    areaLabel: "Organization Settings",
    title: "Privacy, Retention & Data Rights",
    description:
      "Configure approved data purposes, retention, deletion, legal hold, subject rights, export controls and cross-platform data boundaries.",
    group: "Privacy",
    icon: "shield",
    layout: "policy",
    model: "Organization Privacy, Retention and Data-Right Policy Console",
    primaryAction: "Save privacy policy",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Record classes", value: "5", trend: "Updated today", icon: "shield" },
      { label: "Retention rules", value: "92%", trend: "On target", icon: "chart" },
      { label: "Legal holds", value: "268", trend: "1 exception", icon: "check" },
      { label: "Requests", value: "83%", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Policy / Control" },
      { key: "version", label: "Version" },
      { key: "owner", label: "Owner" },
      { key: "deadline", label: "Review date" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SETTINGS-PRIVACY-01",
        name: "Learner progress retention",
        owner: "Ronald Isabirye",
        scope: "Organization-wide",
        status: "Approved",
        progress: 70,
        people: 34,
        updated: "Today 09:22",
        role: "Owner",
        value: "5",
        target: "90%",
        level: "Advanced",
        trend: "+8% this cycle",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "70%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Privacy",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SETTINGS-PRIVACY-02",
        name: "Class recording retention",
        owner: "Amina Mutesi",
        scope: "Operations team",
        status: "Active",
        progress: 81,
        people: 51,
        updated: "Today 08:40",
        role: "Manager",
        value: "68%",
        target: "100%",
        level: "Established",
        trend: "3 actions due",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "81%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SETTINGS-PRIVACY-03",
        name: "Labs evidence retention",
        owner: "Grace Namuli",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 92,
        people: 68,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "4",
        target: "UGX 200M",
        level: "Developing",
        trend: "Evidence current",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "92%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SETTINGS-PRIVACY-04",
        name: "Audit record retention",
        owner: "Mariam Nansubuga",
        scope: "People & Culture",
        status: "Pending",
        progress: 60,
        people: 85,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "92%",
        target: "0 exceptions",
        level: "Initial",
        trend: "Within approved threshold",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "60%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SETTINGS-PRIVACY-05",
        name: "Data subject request workflow",
        owner: "David Kato",
        scope: "Organization-wide",
        status: "Approved",
        progress: 71,
        people: 102,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "14",
        target: "85%",
        level: "Optimized",
        trend: "Human review required",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "71%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Policy or control name",
      "Version",
      "Owner",
      "Effective date",
      "Review date",
      "Approval reason",
    ],
    chart: [62, 69, 76, 83, 90, 49, 56, 63],
    external: null,
    signature:
      "organization::settings::privacy::organization-privacy,-retention-and-data-right-policy-console::v2",
    accessRoles: ["owner", "governance-lead", "technical-admin", "auditor"],
    manageRoles: ["owner", "governance-lead"],
    approveRoles: ["owner", "governance-lead"],
  },
  "settings-branding": {
    id: "settings-branding",
    area: "settings",
    areaLabel: "Organization Settings",
    title: "Organization Branding",
    description:
      "Configure approved organization name, logo reference, accent, email and report branding without replacing Chatoner product identity.",
    group: "Experience",
    icon: "sparkles",
    layout: "form",
    model: "Organization Co-Branding and Communication Template Studio",
    primaryAction: "Save branding",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Templates", value: "68%", trend: "Evidence current", icon: "sparkles" },
      { label: "Approved assets", value: "14", trend: "Within approved threshold", icon: "chart" },
      { label: "Languages", value: "76%", trend: "Human review required", icon: "check" },
      { label: "Pending review", value: "12", trend: "Updated today", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Configuration field" },
      { key: "value", label: "Value" },
      { key: "owner", label: "Owner" },
      { key: "updated", label: "Updated" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SETTINGS-BRANDING-01",
        name: "Organization display name",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Review due",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Experience",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SETTINGS-BRANDING-02",
        name: "Approved logo reference",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SETTINGS-BRANDING-03",
        name: "Report accent and cover",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Ready",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SETTINGS-BRANDING-04",
        name: "Email footer",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Attention",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SETTINGS-BRANDING-05",
        name: "Class observer label",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Organization display name",
      "Legal name",
      "Primary contact",
      "Country and time zone",
      "Default language",
      "Approved change reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature:
      "organization::settings::branding::organization-co-branding-and-communication-template-studio::v2",
  },
  "settings-security": {
    id: "settings-security",
    area: "settings",
    areaLabel: "Organization Settings",
    title: "Security & Sessions",
    description:
      "Configure MFA, SSO enforcement, session duration, allowed domains, privileged actions, emergency access and security notifications.",
    group: "Security",
    icon: "lock",
    layout: "policy",
    model: "Organization Security, Session and Privileged-Action Control Centre",
    primaryAction: "Save security policy",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "MFA coverage", value: "68%", trend: "Within approved threshold", icon: "lock" },
      { label: "SSO users", value: "14", trend: "Human review required", icon: "chart" },
      { label: "Active sessions", value: "76%", trend: "Updated today", icon: "check" },
      { label: "High-risk actions", value: "12", trend: "On target", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Policy / Control" },
      { key: "version", label: "Version" },
      { key: "owner", label: "Owner" },
      { key: "deadline", label: "Review date" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SETTINGS-SECURITY-01",
        name: "SSO enforcement",
        owner: "Amina Mutesi",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 71,
        people: 35,
        updated: "Today 09:22",
        role: "Owner",
        value: "68%",
        target: "90%",
        level: "Advanced",
        trend: "3 actions due",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "71%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Security",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SETTINGS-SECURITY-02",
        name: "Privileged-role MFA",
        owner: "Grace Namuli",
        scope: "Customer Experience",
        status: "Active",
        progress: 82,
        people: 52,
        updated: "Today 08:40",
        role: "Manager",
        value: "4",
        target: "100%",
        level: "Established",
        trend: "Evidence current",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "82%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SETTINGS-SECURITY-03",
        name: "Session duration",
        owner: "Mariam Nansubuga",
        scope: "Technology & Data",
        status: "Review due",
        progress: 93,
        people: 69,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "92%",
        target: "UGX 200M",
        level: "Developing",
        trend: "Within approved threshold",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "93%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SETTINGS-SECURITY-04",
        name: "Allowed email domains",
        owner: "David Kato",
        scope: "Executive committee",
        status: "Pending",
        progress: 61,
        people: 86,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "14",
        target: "0 exceptions",
        level: "Initial",
        trend: "Human review required",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "61%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SETTINGS-SECURITY-05",
        name: "Emergency access approval",
        owner: "Peter Ouma",
        scope: "October 2026 cohort",
        status: "Approved",
        progress: 72,
        people: 103,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "USD 720K",
        target: "85%",
        level: "Optimized",
        trend: "Updated today",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "72%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Policy or control name",
      "Version",
      "Owner",
      "Effective date",
      "Review date",
      "Approval reason",
    ],
    chart: [63, 70, 77, 84, 91, 50, 57, 64],
    external: null,
    signature:
      "organization::settings::security::organization-security,-session-and-privileged-action-control-centre::v2",
    accessRoles: ["owner", "technical-admin", "governance-lead", "auditor"],
    manageRoles: ["owner", "technical-admin"],
    approveRoles: ["owner", "technical-admin"],
  },
  "settings-audit": {
    id: "settings-audit",
    area: "settings",
    areaLabel: "Organization Settings",
    title: "Settings Audit",
    description:
      "Inspect attributable profile, privacy, security, branding, language, notification and integration configuration changes.",
    group: "Settings Governance",
    icon: "history",
    layout: "table",
    model: "Organization Configuration Change and Approval Ledger",
    primaryAction: "Export settings audit",
    secondaryActions: ["Open details", "Assign owner", "Save view"],
    metrics: [
      { label: "Changes", value: "12", trend: "Updated today", icon: "history" },
      { label: "Approvals", value: "68%", trend: "On target", icon: "chart" },
      { label: "Security events", value: "14", trend: "1 exception", icon: "check" },
      { label: "Exports", value: "76%", trend: "+8% this cycle", icon: "alert" },
    ],
    columns: [
      { key: "name", label: "Record / Event" },
      { key: "owner", label: "Actor / Owner" },
      { key: "scope", label: "Scope" },
      { key: "updated", label: "Time" },
      { key: "status", label: "Status" },
    ],
    rows: [
      {
        id: "SETTINGS-AUDIT-01",
        name: "Primary report language changed",
        owner: "Sarah Atim",
        scope: "People & Culture",
        status: "Pending",
        progress: 68,
        people: 32,
        updated: "Today 09:22",
        role: "Owner",
        value: "12",
        target: "90%",
        level: "Advanced",
        trend: "On target",
        source: "Academy records",
        deadline: "Today",
        date: "24 Aug 2026",
        capacity: 320,
        usage: 268,
        evidence: "68%",
        amount: "UGX 184,000,000",
        provider: "Chatoner Academy",
        code: "4821-771-204",
        latency: "84 ms",
        duration: "75 min",
        language: "English + Luganda",
        category: "Settings Governance",
        version: "v3.0",
        format: "Dashboard + PDF",
      },
      {
        id: "SETTINGS-AUDIT-02",
        name: "SSO certificate rotated",
        owner: "Joseph Okello",
        scope: "Organization-wide",
        status: "Healthy",
        progress: 79,
        people: 49,
        updated: "Today 08:40",
        role: "Manager",
        value: "UGX 184M",
        target: "100%",
        level: "Established",
        trend: "1 exception",
        source: "Class attendance",
        deadline: "27 Aug 2026",
        date: "27 Aug 2026",
        capacity: 120,
        usage: 92,
        evidence: "79%",
        amount: "UGX 72,500,000",
        provider: "Microsoft",
        code: "CAA-AIPB-0184",
        latency: "2 min lag",
        duration: "60 min",
        language: "English",
        category: "Operational",
        version: "v2.2",
        format: "CSV",
      },
      {
        id: "SETTINGS-AUDIT-03",
        name: "Finance role scope updated",
        owner: "Ronald Isabirye",
        scope: "Operations team",
        status: "On track",
        progress: 90,
        people: 66,
        updated: "Yesterday 16:04",
        role: "Reviewer",
        value: "5",
        target: "UGX 200M",
        level: "Developing",
        trend: "+8% this cycle",
        source: "Labs callback",
        deadline: "1 Sep 2026",
        date: "1 Sep 2026",
        capacity: 80,
        usage: 63,
        evidence: "90%",
        amount: "USD 18,600",
        provider: "Workday",
        code: "PO-2026-041",
        latency: "Healthy",
        duration: "42 min",
        language: "Kiswahili",
        category: "Governed",
        version: "v1.6",
        format: "PDF",
      },
      {
        id: "SETTINGS-AUDIT-04",
        name: "Branding approval recorded",
        owner: "Amina Mutesi",
        scope: "Finance & Procurement",
        status: "Review due",
        progress: 58,
        people: 83,
        updated: "20 Aug 14:18",
        role: "Contributor",
        value: "68%",
        target: "0 exceptions",
        level: "Initial",
        trend: "3 actions due",
        source: "Human review",
        deadline: "15 Sep 2026",
        date: "15 Sep 2026",
        capacity: 48,
        usage: 41,
        evidence: "58%",
        amount: "UGX 9,400,000",
        provider: "Moodle",
        code: "RUN-184",
        latency: "4 retries",
        duration: "38 min",
        language: "French",
        category: "Connected",
        version: "2026.08",
        format: "JSON",
      },
      {
        id: "SETTINGS-AUDIT-05",
        name: "Labs retention reviewed",
        owner: "Grace Namuli",
        scope: "People & Culture",
        status: "Active",
        progress: 69,
        people: 100,
        updated: "18 Aug 11:37",
        role: "Observer",
        value: "4",
        target: "85%",
        level: "Optimized",
        trend: "Evidence current",
        source: "Finance ledger",
        deadline: "1 Oct 2026",
        date: "1 Oct 2026",
        capacity: 24,
        usage: 18,
        evidence: "69%",
        amount: "UGX 2,750,000",
        provider: "Organization-managed",
        code: "POL-3.0",
        latency: "No lag",
        duration: "90 min",
        language: "Arabic",
        category: "Commercial",
        version: "Draft 4",
        format: "Scheduled email",
      },
    ],
    tabs: ["Operations", "Decisions", "Evidence"],
    notes: [
      {
        title: "Organization authority boundary",
        body: "Organization roles coordinate sponsorship, access, governance, commercial operations and aggregate outcomes; they do not silently inherit learner, instructor, assessor or live-class authority.",
      },
      {
        title: "Human decision and attribution",
        body: "Consequential approvals, academic outcomes, credential decisions, risk acceptance, payments and access grants require a named authorized person and immutable audit reason.",
      },
      {
        title: "Production integration",
        body: "The unified Academy NestJS backend enforces tenant, role, object, purpose, field, environment, expiry and approval scope; Kafka and Redis remain behind server-side services.",
      },
    ],
    fields: [
      "Name / title",
      "Owner",
      "Organization scope",
      "Status",
      "Effective date",
      "Purpose and audit reason",
    ],
    chart: [60, 67, 74, 81, 88, 47, 54, 61],
    external: null,
    signature: "organization::settings::audit::organization-configuration-change-and-approval-ledger::v2",
    accessRoles: ["owner", "governance-lead", "technical-admin", "auditor"],
    manageRoles: [],
  },
};

export const ORGANIZATION_ROLES: RoleProfile[] = [
  {
    id: "owner",
    name: "Organization Owner",
    scope: "Whole organization tenant",
    areas: ["*"],
    manage: ["*"],
    approve: ["*"],
  },
  {
    id: "program-manager",
    name: "Program Manager",
    scope: "Assigned programs, cohorts and success plan",
    areas: [
      "command",
      "people",
      "learning",
      "class",
      "rooms",
      "labs",
      "projects",
      "reports",
      "support",
      "search",
      "notifications",
      "settings",
    ],
    manage: ["learning", "class", "rooms", "labs", "projects", "reports", "support"],
    approve: ["learning", "projects", "reports"],
  },
  {
    id: "people-admin",
    name: "People Admin",
    scope: "Members, teams, seats, enrolment and identity review",
    areas: [
      "people",
      "learning",
      "integrations",
      "reports",
      "support",
      "search",
      "notifications",
      "settings",
    ],
    manage: ["people", "learning", "integrations"],
    approve: ["people"],
  },
  {
    id: "team-manager",
    name: "Team Manager",
    scope: "Assigned team and sponsored learners",
    areas: [
      "command",
      "people",
      "learning",
      "class",
      "rooms",
      "labs",
      "projects",
      "reports",
      "support",
      "search",
      "notifications",
    ],
    manage: ["people", "learning", "support"],
    approve: [],
  },
  {
    id: "instructor-lead",
    name: "Instructor Lead",
    scope: "Assigned instructors, cohorts, teaching and review capacity",
    areas: [
      "learning",
      "class",
      "rooms",
      "labs",
      "projects",
      "reports",
      "support",
      "search",
      "notifications",
    ],
    manage: ["learning", "class", "rooms", "labs", "projects"],
    approve: ["learning", "projects"],
  },
  {
    id: "governance-lead",
    name: "Governance Lead",
    scope: "Policies, tools, risk, entitlements and audit",
    areas: [
      "command",
      "people",
      "labs",
      "projects",
      "governance",
      "integrations",
      "procurement",
      "reports",
      "support",
      "search",
      "notifications",
      "settings",
    ],
    manage: ["governance", "labs", "integrations", "settings"],
    approve: ["governance", "labs", "procurement"],
  },
  {
    id: "finance-admin",
    name: "Finance Admin",
    scope: "Commercial plan, invoices, usage and renewal",
    areas: ["command", "procurement", "finance", "reports", "support", "search", "notifications", "settings"],
    manage: ["finance", "reports"],
    approve: ["finance"],
  },
  {
    id: "procurement-officer",
    name: "Procurement Officer",
    scope: "Vendor evidence, contracts, DPA, SOW and purchase orders",
    areas: ["governance", "procurement", "finance", "reports", "support", "search", "notifications"],
    manage: ["procurement"],
    approve: ["procurement"],
  },
  {
    id: "technical-admin",
    name: "Technical Admin",
    scope: "SSO, SCIM, integrations, webhooks and service health",
    areas: ["people", "class", "labs", "integrations", "support", "search", "notifications", "settings"],
    manage: ["integrations", "settings"],
    approve: ["integrations"],
  },
  {
    id: "auditor",
    name: "Auditor",
    scope: "Approved organization evidence and audit records",
    areas: ["*"],
    manage: [],
    approve: [],
  },
];

export const LEGACY_ROUTE_ALIASES: Record<string, string> = {
  "command/dashboard": "command-dashboard",
  "command/readiness": "command-readiness",
  "command/adoption": "command-adoption",
  "command/outcomes": "command-outcomes",
  "command/action-centre": "command-action-centre",
  "people/members": "people-members",
  "people/teams": "people-teams",
  "people/learners": "people-learners",
  "people/instructors": "people-instructors",
  "people/roles": "people-roles",
  "people/access": "people-access",
  "learning/programs": "learning-programs",
  "learning/tracks": "learning-tracks",
  "learning/cohorts": "learning-cohorts",
  "learning/assignments": "learning-assignments",
  "learning/assessments": "learning-assessments",
  "learning/calendar": "learning-calendar",
  "class/connection": "class-connection",
  "class/sessions": "class-sessions",
  "class/attendance": "class-attendance",
  "class/recordings": "class-recordings",
  "class/office-hours": "class-office-hours",
  "rooms/portfolio": "rooms-portfolio",
  "rooms/room": "rooms-room",
  "rooms/groups": "rooms-groups",
  "rooms/evidence": "rooms-evidence",
  "rooms/permissions": "rooms-permissions",
  "labs/connection": "labs-connection",
  "labs/catalogue": "labs-catalogue",
  "labs/entitlements": "labs-entitlements",
  "labs/runs": "labs-runs",
  "labs/evidence": "labs-evidence",
  "labs/analytics": "labs-analytics",
  "projects/portfolio": "projects-portfolio",
  "projects/workflow-projects": "projects-workflow-projects",
  "projects/capstones": "projects-capstones",
  "projects/credentials": "projects-credentials",
  "projects/evidence": "projects-evidence",
  "governance/overview": "governance-overview",
  "governance/policies": "governance-policies",
  "governance/approved-tools": "governance-approved-tools",
  "governance/risk-register": "governance-risk-register",
  "governance/acknowledgements": "governance-acknowledgements",
  "governance/audit": "governance-audit",
  "integrations/catalogue": "integrations-catalogue",
  "integrations/identity": "integrations-identity",
  "integrations/learning": "integrations-learning",
  "integrations/collaboration": "integrations-collaboration",
  "integrations/automation": "integrations-automation",
  "integrations/health": "integrations-health",
  "procurement/overview": "procurement-overview",
  "procurement/documents": "procurement-documents",
  "procurement/contracts": "procurement-contracts",
  "procurement/purchase-orders": "procurement-purchase-orders",
  "procurement/approvals": "procurement-approvals",
  "finance/overview": "finance-overview",
  "finance/plan": "finance-plan",
  "finance/invoices": "finance-invoices",
  "finance/usage": "finance-usage",
  "finance/renewal": "finance-renewal",
  "reports/library": "reports-library",
  "reports/executive": "reports-executive",
  "reports/learning": "reports-learning",
  "reports/adoption": "reports-adoption",
  "reports/governance": "reports-governance",
  "reports/custom": "reports-custom",
  "support/success-plan": "support-success-plan",
  "support/tickets": "support-tickets",
  "support/contacts": "support-contacts",
  "support/resources": "support-resources",
  "support/service-status": "support-service-status",
  "search/global": "search-global",
  "notifications/inbox": "notifications-inbox",
  "settings/profile": "settings-profile",
  "settings/preferences": "settings-preferences",
  "settings/privacy": "settings-privacy",
  "settings/branding": "settings-branding",
  "settings/security": "settings-security",
  "settings/audit": "settings-audit",
};
